import React, { useState, useEffect, useRef } from 'react';
import { View, Text, StyleSheet, TextInput, TouchableOpacity, ScrollView, Alert, Modal, Platform } from 'react-native';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import AnimatedCard from './AnimatedCard';
import { persistentWidgetService } from '../services/persistentWidgetService';

// Urgency options for Strategic Action Matrix
const URGENCY_OPTIONS = [
  { value: 1, label: 'Not Urgent: No time pressure; deadlines far out; delaying has little consequence.', score: 1.0 },
  { value: 2, label: 'Low Urgency: Deadline approaching but flexible; can wait without major impact.', score: 2.0 },
  { value: 3, label: 'Urgent: Time-sensitive; deadline soon; delaying creates clear problems.', score: 3.0 },
  { value: 4, label: 'Very Urgent: Requires immediate action; deadline critical; delaying creates significant risk or blockage.', score: 4.0 },
];

// Importance options for Strategic Action Matrix
const IMPORTANCE_OPTIONS = [
  { value: 1, label: 'Not Important: Minimal impact; little effect on goals, outcomes, or results.', score: 1.0 },
  { value: 2, label: 'Low Importance: Some value but not strategic; limited impact if delayed or skipped', score: 2.0 },
  { value: 3, label: 'Important: Contributes directly to key goals, deliverables, or priorities; matters to success.', score: 3.0 },
  { value: 4, label: 'Very Important: High-impact, strategic, or mission-critical work; essential to major outcomes.', score: 4.0 },
];

// Attribute definitions with rank options
const ATTRIBUTES = [
  {
    name: 'Revenue Growth and/or New Clients',
    definition: 'Measures the initiative\'s ability to drive financial growth through increased revenue, new contracts, expanded markets, additional clients (and client retention), increased client satisfaction, and opportunities to cross-sell or upsell billable services.',
    rankOptions: [
      { value: 1, label: 'Minimal impact: Little to no revenue upside; no meaningful new clients.', score: 1.0 },
      { value: 2, label: 'Low impact: Modest revenue lift; limited or pilot-level new clients.', score: 2.0 },
      { value: 3, label: 'Moderate impact: Steady revenue growth; moderate expansion of clients or segments.', score: 3.0 },
      { value: 4, label: 'High impact: Strong revenue increase; major client wins or segment growth.', score: 4.0 },
      { value: 5, label: 'Transformational impact: Material revenue shift; multiple strategic clients or large-scale expansion.', score: 5.0 }
    ]
  },
  {
    name: 'Confidence',
    definition: 'Assessment of the likelihood of it happening. Confidence level in all other Attribute Estimates for this initiative/opportunity/project.',
    rankOptions: [
      { value: 1, label: 'Very Low Confidence: Major unknowns; little supporting data; unlikely to achieve projected results.', score: 1.0 },
      { value: 2, label: 'Low Confidence: Several uncertainties; limited validation; outcomes questionable.', score: 2.0 },
      { value: 3, label: 'Moderate Confidence: Assumptions reasonable; some validation; results plausible but not assured.', score: 3.0 },
      { value: 4, label: 'High Confidence: Strong evidence; assumptions mostly validated; results likely.', score: 4.0 },
      { value: 5, label: 'Very High Confidence: Clear data and precedent; assumptions solid; results very likely.', score: 5.0 }
    ]
  },
  {
    name: 'Profitability',
    definition: 'Evaluates the margin contribution of the initiative. Considers cost-to-serve, operational complexity, staffing intensity, technology investment required, and long-term financial sustainability.',
    rankOptions: [
      { value: 1, label: 'Negative or very low margin', score: 1.0 },
      { value: 2, label: 'Near break-even / low positive margin', score: 2.0 },
      { value: 3, label: 'Moderate margin contribution', score: 3.0 },
      { value: 4, label: 'Strong margin contribution', score: 4.0 },
      { value: 5, label: 'Very high / strategic margin contribution', score: 5.0 }
    ]
  },
  {
    name: 'Stickiness',
    definition: 'Measures how much the initiative strengthens retention, deepens client reliance, or increases switching costs. Includes relationship durability, contract renewal likelihood, and long-term engagement benefits.',
    rankOptions: [
      { value: 1, label: 'Little to no impact on retention', score: 1.0 },
      { value: 2, label: 'Small improvement in retention / engagement', score: 2.0 },
      { value: 3, label: 'Moderate improvement in retention; some switching friction', score: 3.0 },
      { value: 4, label: 'Strong retention driver; meaningful lock-in', score: 4.0 },
      { value: 5, label: 'Critical to client relationship; very high retention lock-in', score: 5.0 }
    ]
  },
  {
    name: 'Market Differentiation',
    definition: 'Assesses how much the initiative differentiates your company from competitors or creates unique advantages. Includes innovation, unique capabilities, exclusive clinical programs, and strategic branding impact.',
    rankOptions: [
      { value: 1, label: 'No real differentiation', score: 1.0 },
      { value: 2, label: 'Slight differentiation vs. competitors', score: 2.0 },
      { value: 3, label: 'Noticeable differentiation on some dimensions', score: 3.0 },
      { value: 4, label: 'Strong differentiation; clear unique value', score: 4.0 },
      { value: 5, label: 'Highly differentiated / category-defining', score: 5.0 }
    ]
  }
];

// Dropdown Component - Custom React component on web, modal on mobile
const Dropdown = ({ value, options, onSelect, style, palette, showBadge = false, collapseUp = false, placeholder = "Select...", disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedOption = options.find(opt => opt.value === value) || null;
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (Platform.OS === 'web' && isOpen) {
      const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
          setIsOpen(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  // Use custom React dropdown on web
  if (Platform.OS === 'web') {
    const paddingLeft = showBadge && selectedOption ? 48 : 12;
    const paddingRight = 40;
    const paddingTop = 10;
    const paddingBottom = 10;

    return (
      <View style={[styles.dropdownOuterWrapper, { zIndex: isOpen ? 9999 : 1 }, style]}>
        <View style={styles.dropdownInnerWrapper} ref={dropdownRef}>
          <TouchableOpacity
            onPress={() => !disabled && setIsOpen(!isOpen)}
            style={[
              styles.webSelectButton,
              {
                paddingLeft,
                paddingRight,
                paddingTop,
                paddingBottom,
                backgroundColor: palette.surface || '#1a1a1a',
                borderColor: palette.border || 'rgba(255,255,255,0.2)',
              }
            ]}
            activeOpacity={0.8}
          >
            <Text
              style={[styles.webSelectText, { color: palette.text || 'white' }]}
              numberOfLines={1}
            >
              {selectedOption ? selectedOption.label : placeholder}
            </Text>
          </TouchableOpacity>
          {showBadge && selectedOption && (
            <View 
              style={[
                styles.rankBadge, 
                { 
                  backgroundColor: palette.primary || '#FF0600',
                  position: 'absolute',
                  left: 10,
                  top: '50%',
                  transform: [{ translateY: -14 }],
                  zIndex: 1,
                }
              ]}
            >
              <Text style={styles.rankBadgeText}>{selectedOption.value}</Text>
            </View>
          )}
          {!disabled && (
            <View style={styles.webSelectArrow}>
              <Text style={[styles.webSelectArrowText, { color: palette.mutedText || 'rgba(255,255,255,0.7)' }]}>▼</Text>
            </View>
          )}
          {isOpen && !disabled && (
            <View
              style={[
                styles.webDropdownMenu,
                collapseUp ? styles.webDropdownMenuAbove : styles.webDropdownMenuBelow,
                {
                  backgroundColor: palette.surfaceSolid || '#ffffff',
                  borderColor: '#000000',
                }
              ]}
              pointerEvents="auto"
            >
              <ScrollView
                style={[styles.webDropdownScroll, { backgroundColor: palette.surfaceSolid || '#ffffff' }]}
                nestedScrollEnabled={true}
              >
                {options.map((option) => (
                  <TouchableOpacity
                    key={option.value}
                    onPress={(e) => {
                      e.stopPropagation && e.stopPropagation();
                      onSelect(option.value);
                      setIsOpen(false);
                    }}
                    onPressIn={(e) => e.stopPropagation && e.stopPropagation()}
                    style={[
                      styles.webDropdownOption,
                      {
                        borderBottomColor: palette.border || 'rgba(0,0,0,0.1)',
                        backgroundColor: value === option.value ? (palette.primary || '#FF0600') + '20' : 'transparent',
                      }
                    ]}
                    activeOpacity={0.7}
                  >
                    <Text style={[styles.webDropdownOptionText, { color: palette.text || '#000000' }]}>
                      {option.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>
          )}
        </View>
      </View>
    );
  }

  // Modal for mobile
  return (
    <View style={[styles.dropdownContainer, style]}>
      <TouchableOpacity
        style={[styles.dropdownButton, { borderColor: palette.border || 'rgba(255,255,255,0.2)' }]}
        onPress={() => setIsOpen(true)}
      >
        <View style={styles.dropdownButtonContent}>
          {showBadge && selectedOption && (
            <View style={[styles.rankBadge, { backgroundColor: palette.primary || '#FF0600' }]}>
              <Text style={styles.rankBadgeText}>{selectedOption.value}</Text>
            </View>
          )}
          <Text style={[styles.dropdownText, { color: palette.text || 'white', flex: 1 }]}>
            {selectedOption ? selectedOption.label : 'Select...'}
          </Text>
          <Text style={[styles.dropdownArrow, { color: palette.mutedText || 'rgba(255,255,255,0.7)' }]}>▼</Text>
        </View>
      </TouchableOpacity>
      {selectedOption && (
        <View style={styles.dropdownDescription}>
          <Text style={[styles.dropdownDescriptionText, { color: palette.mutedText || 'rgba(255,255,255,0.7)' }]}>
            {selectedOption.label}
          </Text>
        </View>
      )}
      
      <Modal
        visible={isOpen}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setIsOpen(false)}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setIsOpen(false)}
        >
          <View style={[styles.modalContent, { backgroundColor: palette.surfaceSolid || '#1a1a1a' }]}>
            {options.map((option) => (
              <TouchableOpacity
                key={option.value}
                style={[
                  styles.dropdownOption,
                  { 
                    backgroundColor: value === option.value ? (palette.primary || '#FF0600') + '20' : 'transparent',
                    borderColor: palette.border || 'rgba(255,255,255,0.1)'
                  }
                ]}
                onPress={() => {
                  onSelect(option.value);
                  setIsOpen(false);
                }}
              >
                <Text style={[styles.dropdownOptionText, { color: palette.text || 'white' }]}>
                  {option.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </TouchableOpacity>
      </Modal>
    </View>
  );
};

/**
 * Strategic Decision Matrix Widget
 * Helps users evaluate initiatives based on fixed attributes
 */
export const StrategicDecisionMatrix = ({ widget, palette, onComplete, compact = false, reviewMode = false }) => {
  const { user } = useAuth();
  const [initiatives, setInitiatives] = useState([]);
  const [attributeWeights, setAttributeWeights] = useState({}); // { attrIdx: weight } - global weights per attribute (1-5, no repeats)
  const [ranks, setRanks] = useState({}); // { initiativeIdx-attrIdx: rank }
  const [weightedScores, setWeightedScores] = useState({}); // { initiativeIdx-attrIdx: score }
  const [totalScores, setTotalScores] = useState({}); // { initiativeIdx: total }
  const [showResults, setShowResults] = useState(false); // Track if results view is shown

  useEffect(() => {
    // Load completion or initialize with one blank initiative
    if (initiatives.length === 0) {
      setInitiatives([{ title: '' }]);
    }
  }, [widget.id, user?.uid]);

  useEffect(() => {
    calculateWeightedScores();
  }, [initiatives, attributeWeights, ranks]);


  const handleWeightChange = (attrIdx, newWeight) => {
    const weightNum = parseInt(newWeight) || 0;
    if (weightNum < 1 || weightNum > 5) return;
    
    // Check if this weight is already used by another attribute
    const usedWeights = Object.values(attributeWeights);
    if (usedWeights.includes(weightNum) && attributeWeights[attrIdx] !== weightNum) {
      Alert.alert('Invalid Weight', 'This weight is already assigned to another attribute. Weights must be unique (1-5).');
      return;
    }
    
    setAttributeWeights({ ...attributeWeights, [attrIdx]: weightNum });
  };

  const loadCompletion = async () => {
    if (!user?.uid || !widget.id) return;
    
    const periodKey = persistentWidgetService.getPeriodKey(widget.schedule?.type || 'weekly', new Date(), widget.schedule);
    const { completion } = await persistentWidgetService.getLatestCompletionInPeriod(
      widget.id,
      user.uid,
      periodKey
    );

    if (completion?.data) {
      setInitiatives(completion.data.initiatives || []);
      setAttributeWeights(completion.data.attributeWeights || {});
      setRanks(completion.data.ranks || {});
    } else {
      setInitiatives([{ title: '' }]);
    }
  };

  useEffect(() => {
    if (reviewMode) {
      loadCompletion();
    }
  }, [widget.id, user?.uid]);

  const calculateWeightedScores = () => {
    const newWeightedScores = {};
    const newTotalScores = {};

    initiatives.forEach((initiative, iIdx) => {
      let total = 0;
      ATTRIBUTES.forEach((attr, aIdx) => {
        const key = `${iIdx}-${aIdx}`;
        const weight = attributeWeights[aIdx] || 1; // Global weight for this attribute
        const rank = ranks[key] || 1;
        const score = weight * rank;
        newWeightedScores[key] = score;
        total += score;
      });
      newTotalScores[iIdx] = total;
    });

    setWeightedScores(newWeightedScores);
    setTotalScores(newTotalScores);
  };

  const handleComplete = async () => {
    if (!user?.uid) {
      Alert.alert('Error', 'Please log in to save your progress');
      return;
    }

    const periodKey = persistentWidgetService.getPeriodKey(widget.schedule?.type || 'weekly', new Date(), widget.schedule);
    const completionData = {
      widgetId: widget.id,
      userId: user.uid,
      periodKey,
      widgetType: 'strategic_decision',
      data: {
        initiatives,
        attributeWeights,
        ranks,
        weightedScores,
        totalScores,
      },
    };

    const result = await persistentWidgetService.saveCompletion(completionData);
    if (result.success) {
      // Call onComplete callback to mark widget as completed in context
      if (onComplete) {
        onComplete();
      }
    } else {
      Alert.alert('Error', result.error || 'Failed to save');
    }
  };

  const addInitiative = () => {
    setInitiatives([...initiatives, { title: '' }]);
  };

  const removeInitiative = (index) => {
    if (initiatives.length > 1) {
      const newInitiatives = initiatives.filter((_, i) => i !== index);
      setInitiatives(newInitiatives);
      
      // Clean up related data
      const newRanks = { ...ranks };
      Object.keys(newRanks).forEach(key => {
        if (key.startsWith(`${index}-`)) delete newRanks[key];
      });
      setRanks(newRanks);
    }
  };

  const getRankedResults = () => {
    return initiatives
      .map((initiative, idx) => ({
        index: idx,
        title: initiative.title || `Initiative ${idx + 1}`,
        score: totalScores[idx] || 0
      }))
      .sort((a, b) => b.score - a.score)
      .map((item, rank) => ({ ...item, rank: rank + 1 }));
  };

  // Check if matrix is ready to show results (has at least one initiative with title and all attributes ranked)
  const isReadyForResults = () => {
    if (initiatives.length === 0) return false;
    
    // Check if at least one initiative has a title
    const hasTitledInitiative = initiatives.some(init => init.title && init.title.trim() !== '');
    if (!hasTitledInitiative) return false;
    
    // Check if all attributes have weights
    const allWeightsSet = ATTRIBUTES.every((_, aIdx) => attributeWeights[aIdx] && attributeWeights[aIdx] >= 1 && attributeWeights[aIdx] <= 5);
    if (!allWeightsSet) return false;
    
    // Check if at least one initiative has all attributes ranked
    const hasCompleteInitiative = initiatives.some((init, iIdx) => {
      if (!init.title || init.title.trim() === '') return false;
      return ATTRIBUTES.every((_, aIdx) => {
        const key = `${iIdx}-${aIdx}`;
        return ranks[key] !== undefined && ranks[key] !== null;
      });
    });
    
    return hasCompleteInitiative;
  };

  // Render results view
  const renderResultsView = () => {
    const rankedResults = getRankedResults();
    
    return (
      <View style={styles.resultsViewContainer}>
        <View style={styles.resultsHeader}>
          <Text style={[styles.resultsViewTitle, { color: palette.text || 'white' }]}>
            🎯 Results
          </Text>
          <Text style={[styles.resultsViewSubtitle, { color: palette.mutedText || 'rgba(255,255,255,0.7)' }]}>
            Priority Ranking
          </Text>
        </View>

        <ScrollView style={styles.resultsScrollView} showsVerticalScrollIndicator={true}>
          {rankedResults?.length == 0 && (
          <Text style={[styles.resultsViewSubtitle, { color: 'white', textAlign: 'center', marginTop: 20 }]}>
            No results yet. Please fill up the weights and ranks for each initiative/project.
          </Text>
          )}
          {rankedResults.map((result, idx) => {
            const initiative = initiatives[result.index];
            if (!initiative.title || initiative.title.trim() === '') return null;
            
            // Get medal/icon for top 3
            const getRankIcon = (rank) => {
              if (rank === 1) return '🥇';
              if (rank === 2) return '🥈';
              if (rank === 3) return '🥉';
              return `${rank}.`;
            };

            return (
              <View 
                key={result.index} 
                style={[
                  styles.resultCard, 
                  { 
                    backgroundColor: palette.surface,
                    borderColor: result.rank <= 3 ? palette.primary : palette.border || 'rgba(255,255,255,0.16)',
                    borderWidth: result.rank <= 3 ? 2 : 1,
                  }
                ]}
              >
                <View style={styles.resultCardHeader}>
                  <View style={styles.resultRankContainer}>
                    <Text style={[styles.resultRankIcon, { color: palette.primary || '#FF0600' }]}>
                      {getRankIcon(result.rank)}
                    </Text>
                    <Text style={[styles.resultRankText, { color: palette.primary || '#FF0600' }]}>
                      {result.rank}{result.rank === 1 ? 'st' : result.rank === 2 ? 'nd' : result.rank === 3 ? 'rd' : 'th'} Priority
                    </Text>
                  </View>
                  <View style={[styles.resultScoreBadge, { backgroundColor: palette.primary || '#FF0600' }]}>
                    <Text style={styles.resultScoreBadgeText}>
                      {result.score} pts
                    </Text>
                  </View>
                </View>
                <Text style={[styles.resultCardTitle, { color: palette.text || 'white' }]}>
                  {result.title}
                </Text>
              </View>
            );
          })}
        </ScrollView>

        <View style={styles.resultsActions}>
          <TouchableOpacity
            onPress={() => setShowResults(false)}
            style={[styles.resultsButton, styles.goBackButton, { 
              borderColor: palette.border || 'rgba(255,255,255,0.16)',
              backgroundColor: 'transparent'
            }]}
          >
            <Text style={[styles.resultsButtonText, { color: palette.text || 'white' }]}>
              Go Back
            </Text>
          </TouchableOpacity>
          {!reviewMode && (
            <TouchableOpacity
              onPress={handleComplete}
              style={[styles.resultsButton, styles.completeButton, { backgroundColor: palette.primary || '#FF0600' }]}
            >
              <Text style={styles.resultsButtonText}>
                Complete
              </Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    );
  };

  // If showing results, render results view
  if (showResults) {
    return (
      <View style={[styles.widgetCard, { backgroundColor: palette.surface, borderColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
        {renderResultsView()}
      </View>
    );
  }

  return (
    <View style={[styles.widgetCard, { backgroundColor: palette.surface, borderColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
      <View style={styles.widgetHeaderContainer}>
        <View style={styles.widgetHeader}>
          <Text style={[styles.widgetTitle, { color: palette.text || 'white' }]}>
            🎯 {widget.title || `Strategic Decision Matrix ${reviewMode ? ' - Review Mode' : ''}`}
          </Text>
          {widget.description && (
            <Text style={[styles.widgetDescription, { color: palette.mutedText || 'rgba(255,255,255,0.7)' }]}>
              {widget.description}
            </Text>
          )}
        </View>
        {/* Add Initiative Button Column */}
        <View style={styles.addColumn}>
          {!reviewMode && (
          <TouchableOpacity
            onPress={addInitiative}
            style={styles.addInitiativeButtonStyled}
          >
            <Text style={styles.addInitiativeText}>+ Add Initiative</Text>
          </TouchableOpacity>
          )}
        </View>
      </View>
      <View style={[styles.decisionMatrixContainer, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
        {/* Entire matrix is horizontally scrollable */}
        <ScrollView 
          horizontal
          style={styles.decisionMatrixHorizontalScroll}
          showsHorizontalScrollIndicator={true}
          nestedScrollEnabled={true}
        >
          <ScrollView 
            style={styles.verticalScrollWrapper}
            showsVerticalScrollIndicator={true}
            nestedScrollEnabled={true}
          >
            <View style={styles.matrixRowContainer}>
              {/* Fixed Left Section: Attribute and Weight Columns */}
              <View style={styles.fixedLeftSection}>
                {/* Fixed Attribute Name Column */}
                <View style={[styles.fixedAttributeColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                  <View style={[styles.header, styles.attributeHeader, { borderColor: palette.border || 'rgba(255,255,255,0.1)', backgroundColor: palette.surface || 'rgba(255,255,255,0.05)' }]}>
                    <Text style={[styles.attributeHeaderText, { color: palette.text || 'white' }]}>
                      Attribute
                    </Text>
                  </View>
                  {ATTRIBUTES.map((attr, aIdx) => (
                    <View key={aIdx} style={[styles.row, styles.attributeNameRow, { borderColor: palette.border || 'rgba(255,255,255,0.1)', backgroundColor: palette.surface || 'rgba(255,255,255,0.05)' }]}>
                      <Text style={[styles.attributeName, { color: palette.text || 'white' }]}>
                        {attr.name}
                      </Text>
                      <Text style={[styles.attributeDefinition, { color: palette.mutedText || 'rgba(255,255,255,0.7)' }]}>
                        {attr.definition}
                      </Text>
                    </View>
                  ))}
                  <View style={[styles.totalRow, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                    <Text style={[styles.totalLabel, { color: palette.text || 'white' }]}>Total Score</Text>
                  </View>
                </View>

                {/* Fixed Weight Column */}
                <View style={[styles.fixedWeightColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                  <View style={[styles.header, styles.attributeHeader, { borderColor: palette.border || 'rgba(255,255,255,0.1)', backgroundColor: palette.surface || 'rgba(255,255,255,0.05)' }]}>
                    <Text style={[styles.attributeHeaderText, { color: palette.text || 'white' }]}>
                      Weight
                    </Text>
                  </View>
                  {ATTRIBUTES.map((attr, aIdx) => (
                    <View key={aIdx} style={[styles.row, styles.weightRow, { borderColor: palette.border || 'rgba(255,255,255,0.1)', backgroundColor: palette.surface || 'rgba(255,255,255,0.05)' }]}>
                      <TextInput
                        style={[styles.weightInput, { backgroundColor: palette.surfaceSolid || 'rgba(255,255,255,0.05)', color: palette.text || 'white', borderColor: palette.border || 'rgba(255,255,255,0.2)' }]}
                        value={attributeWeights[aIdx]?.toString() || ''}
                        editable={!reviewMode}
                        onChangeText={(value) => handleWeightChange(aIdx, value)}
                        keyboardType="numeric"
                        placeholder="1-5"
                        placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.4)'}
                        maxLength={1}
                      />
                    </View>
                  ))}
                  <View style={[styles.totalRow, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                  </View>
                </View>
              </View>

              {/* Initiative Columns */}
              <View style={styles.initiativesContainer}>
                {initiatives.map((initiative, iIdx) => (
                  <View key={iIdx} style={[styles.initiativeColumn, { zIndex: Math.round(100 / (iIdx + 1)), borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                    {/* Column Header */}
                    <View style={[styles.header, styles.initiativeHeader, { borderColor: palette.border || 'rgba(255,255,255,0.1)', backgroundColor: palette.surface || 'rgba(255,255,255,0.05)' }]}>
                      <View style={styles.headerContent}>
                        <TextInput
                          style={[styles.initiativeTitleInput, { color: palette.text || 'white' }]}
                          value={initiative.title}
                          editable={!reviewMode}
                          onChangeText={(value) => {
                            const newInitiatives = [...initiatives];
                            newInitiatives[iIdx] = { ...newInitiatives[iIdx], title: value };
                            setInitiatives(newInitiatives);
                          }}
                          placeholder="Enter Project/Initiative/Opportunity Title"
                          placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.4)'}
                        />
                        {!reviewMode && initiatives.length > 1 && (
                          <TouchableOpacity onPress={() => removeInitiative(iIdx)} style={styles.removeButton}>
                            <Text style={[styles.removeButtonText, { color: '#EA806B' }]}>✕</Text>
                          </TouchableOpacity>
                        )}
                      </View>
                    </View>

                    {/* Rank and Score for each attribute */}
                    <View style={styles.initiativeRankRowContainer}>
                      <View style={[styles.initiativeRankRow, { zIndex: 200, width: '80%' }]}>
                        {ATTRIBUTES.map((attr, aIdx) => {
                          const key = `${iIdx}-${aIdx}`;
                          const rank = ranks[key] || null;
                          const isLastAttribute = aIdx >= ATTRIBUTES.length - 2;
                          return (
                            <Dropdown
                              key={aIdx}
                              placeholder="Select Rank..."
                              value={rank}
                              options={attr.rankOptions}
                              disabled={reviewMode}
                              onSelect={(value) => setRanks({ ...ranks, [key]: value })}
                              palette={palette}
                              style={styles.row}
                              showBadge={true}
                              collapseUp={isLastAttribute}
                            />
                          );
                        })}
                      </View>
                      <View style={[styles.initiativeRankRow, { zIndex: 100, width: '20%' }]}>
                        {ATTRIBUTES.map((attr, aIdx) => {
                          const key = `${iIdx}-${aIdx}`;
                          const weightedScore = weightedScores[key] || 0;
                          return (
                            <View key={aIdx} style={[styles.row, styles.initiativeRow, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                              <View style={[styles.initiativeCell, styles.scoreCell]}>
                                <Text style={[styles.scoreText, { color: palette.primary || '#FF0600' }]}>{weightedScore}</Text>
                              </View>
                            </View>
                          );
                        })}
                      </View>
                    </View>

                    {/* Total Score */}
                    <View style={[styles.row, styles.initiativeTotalRow, { zIndex: 100, borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                      <Text style={[styles.totalScoreText, { color: palette.primary || '#FF0600' }]}>{totalScores[iIdx] || 0}</Text>
                    </View>
                  </View>
                ))}
              </View>
            </View>
          </ScrollView>
        </ScrollView>
      </View>

      {/* Show Results Button */}
      <TouchableOpacity 
        onPress={() => setShowResults(true)} 
        style={[
          styles.showResultsButton, 
          { 
            backgroundColor: palette.primary || '#FF0600',
            // opacity: isReadyForResults() ? 1 : 0.5
          }
        ]}
        // disabled={!isReadyForResults()}
      >
        <Text style={[styles.showResultsButtonText, { color: 'white' }]}>
          {'🎯 Show Results'}
          {/* {isReadyForResults() ? '🎯 Show Results' : 'Complete matrix to see results'} */}
        </Text>
      </TouchableOpacity>
    </View>
  );
};

/**
 * Strategic Action Matrix Widget
 * Helps users prioritize tasks based on urgency and importance
 */
export const StrategicActionMatrix = ({ widget, palette, onComplete, compact = false, reviewMode = false }) => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [urgencyScores, setUrgencyScores] = useState({}); // { taskIdx: score }
  const [importanceScores, setImportanceScores] = useState({}); // { taskIdx: score }
  const [showResults, setShowResults] = useState(false);

  const loadCompletion = async () => {
    if (!user?.uid || !widget.id) return;
    
    const periodKey = persistentWidgetService.getPeriodKey(widget.schedule?.type || 'weekly', new Date(), widget.schedule);
    const { completion } = await persistentWidgetService.getLatestCompletionInPeriod(
      widget.id,
      user.uid,
      periodKey
    );

    if (completion?.data) {
      const data = completion.data;
      setTasks(data.tasks || []);
      setUrgencyScores(data.urgencyScores || {});
      setImportanceScores(data.importanceScores || {});
      // Show results view in review mode if data exists
      if (reviewMode && data.tasks && data.tasks.length > 0) {
        setShowResults(true);
      }
    } else {
      // Initialize with one empty task if no completion data
      if (!reviewMode && tasks.length === 0) {
        setTasks([{ title: '' }]);
      }
    }
  };

  useEffect(() => {
    if (reviewMode) {
      loadCompletion();
    } else {
      // Initialize with one empty task
      if (tasks.length === 0) {
        setTasks([{ title: '' }]);
      }
    }
  }, [widget.id, user?.uid, reviewMode]);

  useEffect(() => {
    if (!reviewMode) {
      calculateScores();
    }
  }, [tasks, reviewMode]);

  const calculateScores = () => {
    const newUrgencyScores = {};
    const newImportanceScores = {};
    
    tasks.forEach((task, idx) => {
      const urgencyValue = task.urgency;
      const importanceValue = task.importance;
      
      if (urgencyValue) {
        const urgencyOption = URGENCY_OPTIONS.find(opt => opt.value === urgencyValue);
        newUrgencyScores[idx] = urgencyOption ? urgencyOption.score : 0;
      }
      
      if (importanceValue) {
        const importanceOption = IMPORTANCE_OPTIONS.find(opt => opt.value === importanceValue);
        newImportanceScores[idx] = importanceOption ? importanceOption.score : 0;
      }
    });
    
    setUrgencyScores(newUrgencyScores);
    setImportanceScores(newImportanceScores);
  };

  const getQuadrant = (urgencyValue, importanceValue) => {
    if (!urgencyValue || !importanceValue) return '';
    
    const isHighUrgency = urgencyValue >= 3;
    const isHighImportance = importanceValue >= 3;
    
    if (isHighUrgency && isHighImportance) return 'Do First';
    if (isHighUrgency && !isHighImportance) return 'Do Tomorrow';
    if (!isHighUrgency && isHighImportance) return 'Schedule Later';
    return 'Eliminate';
  };

  const handleComplete = async () => {
    if (!user?.uid) {
      Alert.alert('Error', 'Please log in to save your progress');
      return;
    }

    const periodKey = persistentWidgetService.getPeriodKey(widget.schedule?.type || 'weekly', new Date(), widget.schedule);
    const completionData = {
      widgetId: widget.id,
      userId: user.uid,
      periodKey,
      widgetType: 'strategic_action',
      data: {
        tasks,
        urgencyScores,
        importanceScores,
      },
    };

    const result = await persistentWidgetService.saveCompletion(completionData);
    if (result.success) {
      if (onComplete) {
        onComplete();
      }
    } else {
      Alert.alert('Error', result.error || 'Failed to save');
    }
  };

  const addTask = () => {
    if (tasks.length < 10) {
      setTasks([...tasks, { title: '' }]);
    } else {
      Alert.alert('Limit Reached', 'Maximum of 10 tasks allowed');
    }
  };

  const removeTask = (index) => {
    if (tasks.length > 1) {
      const newTasks = tasks.filter((_, i) => i !== index);
      setTasks(newTasks);
      
      // Clean up related data
      const newUrgencyScores = { ...urgencyScores };
      const newImportanceScores = { ...importanceScores };
      delete newUrgencyScores[index];
      delete newImportanceScores[index];
      setUrgencyScores(newUrgencyScores);
      setImportanceScores(newImportanceScores);
    }
  };

  const getTasksByQuadrant = () => {
    const quadrants = {
      'Do First': [],
      'Do Tomorrow': [],
      'Schedule Later': [],
      'Eliminate': [],
    };
    
    tasks.forEach((task, idx) => {
      if (task.title && task.title.trim() !== '' && task.urgency && task.importance) {
        const quadrant = getQuadrant(task.urgency, task.importance);
        quadrants[quadrant].push({ ...task, index: idx });
      }
    });
    
    return quadrants;
  };

  // Render results view
  const renderResultsView = () => {
    const quadrants = getTasksByQuadrant();
    
    return (
      <View style={styles.resultsViewContainer}>
        <View style={styles.resultsHeader}>
          <Text style={[styles.resultsViewTitle, { color: palette.text || 'white' }]}>
            ⚡ Results
          </Text>
          <Text style={[styles.resultsViewSubtitle, { color: palette.mutedText || 'rgba(255,255,255,0.7)' }]}>
            Urgency vs Importance Matrix
          </Text>
        </View>

        <ScrollView style={styles.resultsScrollView} showsVerticalScrollIndicator={true}>
          {/* Matrix with axes */}
          <View style={styles.actionMatrixContainer}>
            {/* Y-axis label */}
            <View style={styles.yAxisContainer}>
              <Text style={[styles.axisLabel, styles.yAxisLabel, { color: palette.text || 'white' }]}>
                IMPORTANCE
              </Text>
            </View>
            
            {/* Quadrants grid */}
            <View style={styles.quadrantsGrid}>
              {/* X-axis label */}
              <View style={styles.xAxisContainer}>
                <Text style={[styles.axisLabel, { color: palette.text || 'white' }]}>
                  URGENCY
                </Text>
              </View>
              
              {/* Quadrants */}
              <View style={styles.quadrantsRow}>
                {/* Top row: High Importance */}
                <View style={[styles.quadrantBox, { borderColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
                  <Text style={[styles.quadrantTitle, { color: palette.primary || '#FF0600' }]}>
                    Do First
                  </Text>
                  <Text style={[styles.quadrantSubtitle, { color: palette.mutedText || 'rgba(255,255,255,0.7)' }]}>
                    High Urgency + High Importance
                  </Text>
                  <ScrollView style={styles.quadrantTasks}>
                    {quadrants['Do First'].map((task, idx) => (
                      <View key={idx} style={[styles.quadrantTaskItem, { backgroundColor: palette.surface }]}>
                        <Text style={[styles.quadrantTaskText, { color: palette.text || 'white' }]}>
                          {task.title}
                        </Text>
                      </View>
                    ))}
                  </ScrollView>
                </View>
                
                <View style={[styles.quadrantBox, { borderColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
                  <Text style={[styles.quadrantTitle, { color: palette.primary || '#FF0600' }]}>
                    Schedule Later
                  </Text>
                  <Text style={[styles.quadrantSubtitle, { color: palette.mutedText || 'rgba(255,255,255,0.7)' }]}>
                    Low Urgency + High Importance
                  </Text>
                  <ScrollView style={styles.quadrantTasks}>
                    {quadrants['Schedule Later'].map((task, idx) => (
                      <View key={idx} style={[styles.quadrantTaskItem, { backgroundColor: palette.surface }]}>
                        <Text style={[styles.quadrantTaskText, { color: palette.text || 'white' }]}>
                          {task.title}
                        </Text>
                      </View>
                    ))}
                  </ScrollView>
                </View>
              </View>
              
              <View style={styles.quadrantsRow}>
                {/* Bottom row: Low Importance */}
                <View style={[styles.quadrantBox, { borderColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
                  <Text style={[styles.quadrantTitle, { color: palette.primary || '#FF0600' }]}>
                    Do Tomorrow
                  </Text>
                  <Text style={[styles.quadrantSubtitle, { color: palette.mutedText || 'rgba(255,255,255,0.7)' }]}>
                    High Urgency + Low Importance
                  </Text>
                  <ScrollView style={styles.quadrantTasks}>
                    {quadrants['Do Tomorrow'].map((task, idx) => (
                      <View key={idx} style={[styles.quadrantTaskItem, { backgroundColor: palette.surface }]}>
                        <Text style={[styles.quadrantTaskText, { color: palette.text || 'white' }]}>
                          {task.title}
                        </Text>
                      </View>
                    ))}
                  </ScrollView>
                </View>
                
                <View style={[styles.quadrantBox, { borderColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
                  <Text style={[styles.quadrantTitle, { color: palette.primary || '#FF0600' }]}>
                    Eliminate
                  </Text>
                  <Text style={[styles.quadrantSubtitle, { color: palette.mutedText || 'rgba(255,255,255,0.7)' }]}>
                    Low Urgency + Low Importance
                  </Text>
                  <ScrollView style={styles.quadrantTasks}>
                    {quadrants['Eliminate'].map((task, idx) => (
                      <View key={idx} style={[styles.quadrantTaskItem, { backgroundColor: palette.surface }]}>
                        <Text style={[styles.quadrantTaskText, { color: palette.text || 'white' }]}>
                          {task.title}
                        </Text>
                      </View>
                    ))}
                  </ScrollView>
                </View>
              </View>
            </View>
          </View>
        </ScrollView>

        <View style={styles.resultsActions}>
          <TouchableOpacity
            onPress={() => setShowResults(false)}
            style={[styles.resultsButton, styles.goBackButton, { 
              borderColor: palette.border || 'rgba(255,255,255,0.16)',
              backgroundColor: 'transparent'
            }]}
          >
            <Text style={[styles.resultsButtonText, { color: palette.text || 'white' }]}>
              {reviewMode ? 'See Tasks' : 'Go Back'}
            </Text>
          </TouchableOpacity>
          {!reviewMode && (
          <TouchableOpacity
            onPress={handleComplete}
            style={[styles.resultsButton, styles.completeButton, { backgroundColor: palette.primary || '#FF0600' }]}
          >
            <Text style={styles.resultsButtonText}>
              Complete
            </Text>
          </TouchableOpacity>
          )}
        </View>
      </View>
    );
  };

  // If showing results, render results view
  if (showResults) {
  return (
    <View style={[styles.widgetCard, { backgroundColor: palette.surface, borderColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
        {renderResultsView()}
      </View>
    );
  }

  return (
    <View id="aaaa" style={[styles.widgetCard, { backgroundColor: palette.surface, borderColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
      <View style={styles.widgetHeaderContainer}>
      <View style={styles.widgetHeader}>
        <Text style={[styles.widgetTitle, { color: palette.text || 'white' }]}>
          ⚡ {widget.title || 'Strategic Action Matrix'}
        </Text>
        {widget.description && (
          <Text style={[styles.widgetDescription, { color: palette.mutedText || 'rgba(255,255,255,0.7)' }]}>
            {widget.description}
          </Text>
        )}
        </View>
        {/* Add Task Button */}
        <View style={styles.addColumn}>
          {!reviewMode && (
          <TouchableOpacity
            onPress={addTask}
            style={[styles.addInitiativeButton, { backgroundColor: palette.primary || '#FF0600' }]}
            disabled={tasks.length >= 10}
          >
            <Text style={styles.addInitiativeText}>
              {tasks.length >= 10 ? 'Max 10 Tasks' : '+ Add Task'}
            </Text>
          </TouchableOpacity>
          )}
        </View>
      </View>

      <View style={styles.actionMatrixTableContainer}>
        <View style={styles.actionMatrixContent}>
          {/* Fixed Header Row */}
          <View style={styles.fixedHeaderRow}>
            <View style={[styles.taskNameColumn, styles.headerColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
              <View style={[styles.header, styles.taskHeader, { backgroundColor: 'rgba(255,255,255,0.05)' }]}>
                <Text style={[styles.attributeHeaderText, { color: palette.text || 'white' }]}>Task</Text>
              </View>
            </View>
            <View style={[styles.taskColumn, styles.headerColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
              <View style={[styles.header, styles.taskHeader, { backgroundColor: 'rgba(255,255,255,0.05)' }]}>
                <Text style={[styles.attributeHeaderText, { color: palette.text || 'white' }]}>Urgency</Text>
              </View>
            </View>
            <View style={[styles.urgencyScoreColumn, styles.headerColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
              <View style={[styles.header, styles.taskHeader, { backgroundColor: 'rgba(255,255,255,0.05)' }]}>
                <Text style={[styles.attributeHeaderText, { color: palette.text || 'white' }]}>Score</Text>
              </View>
            </View>
            <View style={[styles.taskColumn, styles.headerColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
              <View style={[styles.header, styles.taskHeader, { backgroundColor: 'rgba(255,255,255,0.05)' }]}>
                <Text style={[styles.attributeHeaderText, { color: palette.text || 'white' }]}>Importance</Text>
              </View>
            </View>
            <View style={[styles.urgencyScoreColumn, styles.headerColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
              <View style={[styles.header, styles.taskHeader, { backgroundColor: 'rgba(255,255,255,0.05)' }]}>
                <Text style={[styles.attributeHeaderText, { color: palette.text || 'white' }]}>Score</Text>
              </View>
            </View>
            <View style={[styles.quadrantColumn, styles.headerColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
              <View style={[styles.header, styles.taskHeader, { backgroundColor: 'rgba(255,255,255,0.05)' }]}>
                <Text style={[styles.attributeHeaderText, { color: palette.text || 'white' }]}>Quadrant</Text>
              </View>
            </View>
          </View>

          {/* Content Rows */}
          <ScrollView style={styles.verticalScrollContent} showsVerticalScrollIndicator={true} nestedScrollEnabled={true}>
            <View style={styles.contentRowsContainer}>
              {/* Task Name Column */}
              <View style={[styles.taskNameColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                {tasks.map((task, tIdx) => (
                  <View key={tIdx} style={[styles.taskRow, styles.attributeNameRow, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                    <View style={styles.headerContent}>
                      <TextInput
                        style={[styles.initiativeTitleInput, { color: palette.text || 'white' }]}
                        value={task.title}
                        editable={!reviewMode}
                        onChangeText={(value) => {
                          const newTasks = [...tasks];
                          newTasks[tIdx] = { ...newTasks[tIdx], title: value };
                          setTasks(newTasks);
                        }}
                        placeholder="Enter Task Name"
                        placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.4)'}
                      />
                      {!reviewMode && tasks.length > 1 && (
                        <TouchableOpacity onPress={() => removeTask(tIdx)} style={styles.removeButton}>
                          <Text style={[styles.removeButtonText, { color: '#EA806B' }]}>✕</Text>
                        </TouchableOpacity>
                      )}
                    </View>
                  </View>
                ))}
              </View>
              
              {/* Urgency Column */}
              <View style={[styles.taskColumn, { zIndex: 200, borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                {tasks.map((task, tIdx) => (
                  <Dropdown
                    key={tIdx}
                    value={task.urgency}
                    disabled={reviewMode}
                    options={URGENCY_OPTIONS}
                    onSelect={(value) => {
                      const newTasks = [...tasks];
                      newTasks[tIdx] = { ...newTasks[tIdx], urgency: value };
                      setTasks(newTasks);
                    }}
                    palette={palette}
                    style={styles.taskRow}
                    showBadge={true}
                  />
                ))}
              </View>

              {/* Urgency Score Column */}
              <View style={[styles.urgencyScoreColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                {tasks.map((task, tIdx) => (
                  <View key={tIdx} style={[styles.taskRow, styles.initiativeRow, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                    <View style={[styles.taskScoreCell, styles.scoreCell]}>
                      <Text style={[styles.scoreText, { color: palette.primary || '#FF0600' }]}>{urgencyScores[tIdx] || 0}</Text>
                    </View>
                  </View>
                ))}
              </View>

              {/* Importance Column */}
              <View style={[styles.taskColumn, { zIndex: 100, borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                {tasks.map((task, tIdx) => (
                  <Dropdown
                    key={tIdx}
                    value={task.importance}
                    options={IMPORTANCE_OPTIONS}
                    disabled={reviewMode}
                    onSelect={(value) => {
                      const newTasks = [...tasks];
                      newTasks[tIdx] = { ...newTasks[tIdx], importance: value };
                      setTasks(newTasks);
                    }}
                    palette={palette}
                    style={styles.taskRow}
                    showBadge={true}
                  />
                ))}
              </View>

              {/* Importance Score Column */}
              <View style={[styles.urgencyScoreColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                {tasks.map((task, tIdx) => (
                  <View key={tIdx} style={[styles.taskRow, styles.initiativeRow, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                    <View style={[styles.taskScoreCell, styles.scoreCell]}>
                      <Text style={[styles.scoreText, { color: palette.primary || '#FF0600' }]}>{importanceScores[tIdx] || 0}</Text>
                    </View>
                  </View>
                ))}
              </View>

              {/* Quadrant Column */}
              <View style={[styles.quadrantColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                {tasks.map((task, tIdx) => (
                  <View key={tIdx} style={[styles.taskRow, styles.initiativeRow, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                    <View style={[styles.taskScoreCell, styles.scoreCell]}>
                      <Text style={[styles.scoreText, { color: palette.primary || '#FF0600', fontSize: 12 }]}>
                        {getQuadrant(task.urgency, task.importance) || '-'}
                      </Text>
                    </View>
                  </View>
                ))}
              </View>
            </View>
          </ScrollView>
        </View>
      </View>

      {/* Show Results Button */}
      <TouchableOpacity 
        onPress={() => setShowResults(true)} 
        style={[
          styles.showResultsButton, 
          { 
            backgroundColor: palette.primary || '#FF0600',
          }
        ]}
      >
        <Text style={[styles.showResultsButtonText, { color: 'white' }]}>
          ⚡ Show Results
        </Text>
      </TouchableOpacity>
    </View>
  );
};

/**
 * Tiger Team Breakouts Tracker Widget
 * Performance tracking system with staff management and scoring
 */
export const TigerTeamBreakoutsTracker = ({ widget, palette, onComplete, compact = false, reviewMode = false }) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('all_staff');
  const [allStaff, setAllStaff] = useState([]);
  const [eligibilityData, setEligibilityData] = useState([]);
  const [financeData, setFinanceData] = useState([]);
  const [itData, setItData] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [newDepartment, setNewDepartment] = useState('');

  useEffect(() => {
    loadCompletion();
  }, [widget.id, user?.uid]);

  useEffect(() => {
    // Auto-populate department tabs when allStaff changes
    updateDepartmentTabs();
  }, [allStaff]);

  // Get all staff names for "Reports To" dropdown
  const getStaffNames = () => {
    return [{ value: '', label: 'None (Top Level)' }, ...allStaff.map(s => ({ value: s.person, label: s.person })).filter(s => s.value)];
  };

  const loadCompletion = async () => {
    if (!user?.uid || !widget.id) return;
    
    const periodKey = persistentWidgetService.getPeriodKey(widget.schedule?.type || 'weekly', new Date(), widget.schedule);
    const { completion } = await persistentWidgetService.getLatestCompletionInPeriod(
      widget.id,
      user.uid,
      periodKey
    );

    if (completion?.data) {
      setAllStaff(completion.data.allStaff || []);
      setEligibilityData(completion.data.eligibilityData || []);
      setFinanceData(completion.data.financeData || []);
      setItData(completion.data.itData || []);
      // Load departments - extract unique from data
      if (completion.data.departments && completion.data.departments.length > 0) {
        setDepartments(completion.data.departments);
      } else {
        // Extract unique departments from existing staff data
        const existingDepts = [...new Set((completion.data.allStaff || []).map(s => s.department).filter(Boolean))];
        if (existingDepts.length > 0) {
          setDepartments(existingDepts);
        }
      }
    } else {
      setAllStaff([]);
      setEligibilityData([]);
      setFinanceData([]);
      setItData([]);
    }
  };
  
  // Add new department
  const addDepartment = () => {
    if (newDepartment.trim() && !departments.includes(newDepartment.trim())) {
      setDepartments([...departments, newDepartment.trim()]);
      setNewDepartment('');
    }
  };

  const updateDepartmentTabs = () => {
    // Auto-populate department tabs from allStaff
    const eligibility = allStaff.filter(s => s.department === 'Eligibility' && s.active === 'Y');
    const finance = allStaff.filter(s => s.department === 'Finance' && s.active === 'Y');
    const it = allStaff.filter(s => s.department === 'IT' && s.active === 'Y');

    // Merge with existing data to preserve metrics, but update basic fields from All Staff
    setEligibilityData(prev => {
      const merged = eligibility.map(staff => {
        const existing = prev.find(p => p.person === staff.person);
        if (existing) {
          // Update basic fields from All Staff while preserving metrics
          return {
            ...existing,
            person: staff.person,
            title: staff.title,
            department: staff.department,
            reportsTo: staff.reportsTo,
            type: staff.type,
            active: staff.active,
          };
        }
        // New entry
        return {
          person: staff.person,
          title: staff.title,
          department: staff.department,
          reportsTo: staff.reportsTo,
          type: staff.type,
          active: staff.active,
          q1Measure: '', q1Score: '', q1Weight: '',
          q2Measure: '', q2Score: '', q2Weight: '',
          q3Measure: '', q3Score: '', q3Weight: '',
          qual1Measure: '', qual1Score: '', qual1Weight: '',
        };
      });
      return merged;
    });

    setFinanceData(prev => {
      const merged = finance.map(staff => {
        const existing = prev.find(p => p.person === staff.person);
        if (existing) {
          // Update basic fields from All Staff while preserving metrics
          return {
            ...existing,
            person: staff.person,
            title: staff.title,
            department: staff.department,
            reportsTo: staff.reportsTo,
            type: staff.type,
            active: staff.active,
          };
        }
        // New entry
        return {
          person: staff.person,
          title: staff.title,
          department: staff.department,
          reportsTo: staff.reportsTo,
          type: staff.type,
          active: staff.active,
          q1Measure: '', q1Score: '', q1Weight: '',
          q2Measure: '', q2Score: '', q2Weight: '',
          q3Measure: '', q3Score: '', q3Weight: '',
          qual1Measure: '', qual1Score: '', qual1Weight: '',
        };
      });
      return merged;
    });

    setItData(prev => {
      const merged = it.map(staff => {
        const existing = prev.find(p => p.person === staff.person);
        if (existing) {
          // Update basic fields from All Staff while preserving metrics
          return {
            ...existing,
            person: staff.person,
            title: staff.title,
            department: staff.department,
            reportsTo: staff.reportsTo,
            type: staff.type,
            active: staff.active,
          };
        }
        // New entry
        return {
          person: staff.person,
          title: staff.title,
          department: staff.department,
          reportsTo: staff.reportsTo,
          type: staff.type,
          active: staff.active,
          q1Measure: '', q1Score: '', q1Weight: '',
          q2Measure: '', q2Score: '', q2Weight: '',
          q3Measure: '', q3Score: '', q3Weight: '',
          qual1Measure: '', qual1Score: '', qual1Weight: '',
        };
      });
      return merged;
    });
  };

  // Calculate Individual Score (Weighted)
  const calculateIndividualScore = (personData) => {
    if (!personData) return '';
    
    const weights = [
      parseFloat(personData.q1Weight) || 0,
      parseFloat(personData.q2Weight) || 0,
      parseFloat(personData.q3Weight) || 0,
      parseFloat(personData.qual1Weight) || 0,
    ];
    const scores = [
      parseFloat(personData.q1Score) || 0,
      parseFloat(personData.q2Score) || 0,
      parseFloat(personData.q3Score) || 0,
      parseFloat(personData.qual1Score) || 0,
    ];

    const totalWeight = weights.reduce((sum, w) => sum + w, 0);
    if (totalWeight === 0) return '';

    const weightedSum = weights.reduce((sum, w, i) => sum + (w * scores[i]), 0);
    return (weightedSum / totalWeight).toFixed(1);
  };

  // Calculate Direct Reports Avg Score (with cycle detection)
  const calculateDirectReportsAvg = (managerName, departmentData, visited = new Set(), depth = 0) => {
    // Prevent infinite recursion
    if (depth > 10) return ''; // Max depth limit
    if (visited.has(managerName)) return ''; // Cycle detection
    
    if (!managerName || !departmentData || departmentData.length === 0) return '';
    
    const directReports = departmentData.filter(p => p && p.reportsTo === managerName);
    if (directReports.length === 0) return '';

    const newVisited = new Set(visited);
    newVisited.add(managerName);

    const finalScores = directReports.map(p => {
      if (!p || !p.person) return 0;
      const final = calculateFinalScore(p, departmentData, newVisited, depth + 1);
      return parseFloat(final) || 0;
    }).filter(s => s > 0 && !isNaN(s));

    if (finalScores.length === 0) return '';
    const avg = finalScores.reduce((sum, s) => sum + s, 0) / finalScores.length;
    return isNaN(avg) ? '' : avg.toFixed(1);
  };

  // Calculate Final Score (with cycle detection)
  const calculateFinalScore = (personData, departmentData, visited = new Set(), depth = 0) => {
    // Prevent infinite recursion
    if (depth > 10) {
      // If we hit depth limit, just return individual score
      return calculateIndividualScore(personData);
    }
    
    if (!personData || !personData.person) return '';
    
    // Cycle detection - if this person is already in the chain, return individual score only
    if (visited.has(personData.person)) {
      return calculateIndividualScore(personData);
    }
    
    const individualScore = calculateIndividualScore(personData);
    
    // Only calculate direct reports if we haven't visited this person yet
    const newVisited = new Set(visited);
    newVisited.add(personData.person);
    
    const directReportsAvg = calculateDirectReportsAvg(personData.person, departmentData, newVisited, depth);

    if (!individualScore && !directReportsAvg) return '';
    if (!individualScore) return directReportsAvg;
    if (!directReportsAvg) return individualScore;

    const avg = (parseFloat(individualScore) + parseFloat(directReportsAvg)) / 2;
    return isNaN(avg) ? '' : avg.toFixed(1);
  };

  const handleComplete = async () => {
    if (!user?.uid) {
      Alert.alert('Error', 'Please log in to save your progress');
      return;
    }

    const periodKey = persistentWidgetService.getPeriodKey(widget.schedule?.type || 'weekly', new Date(), widget.schedule);
    const completionData = {
      widgetId: widget.id,
      userId: user.uid,
      periodKey,
      widgetType: 'tiger_team_breakouts',
      data: {
        allStaff,
        eligibilityData,
        financeData,
        itData,
        departments,
      },
    };

    const result = await persistentWidgetService.saveCompletion(completionData);
    if (result.success) {
      if (onComplete) {
        onComplete();
      }
    } else {
      Alert.alert('Error', result.error || 'Failed to save');
    }
  };

  const addStaff = () => {
    setAllStaff([...allStaff, {
      person: '',
      title: '',
      department: 'Eligibility',
      reportsTo: '',
      type: 'FTE',
      active: 'Y',
      flsaStatus: 'Exempt',
    }]);
  };

  const removeStaff = (index) => {
    setAllStaff(allStaff.filter((_, i) => i !== index));
  };

  // Get all staff summary for Summary tab
  const getAllStaffSummary = () => {
    const allData = [...eligibilityData, ...financeData, ...itData];
    return allData.map(person => ({
      department: person.department,
      employeeName: person.person,
      title: person.title,
      managerName: person.reportsTo || '-',
      finalScore: calculateFinalScore(person, 
        person.department === 'Eligibility' ? eligibilityData :
        person.department === 'Finance' ? financeData : itData
      ),
    })).filter(p => p.finalScore);
  };

  // Get performance by department
  const getPerformanceByDepartment = () => {
    const summary = getAllStaffSummary();
    const deptGroups = {};
    summary.forEach(person => {
      if (!deptGroups[person.department]) {
        deptGroups[person.department] = [];
      }
      deptGroups[person.department].push(parseFloat(person.finalScore) || 0);
    });
    
    return Object.entries(deptGroups).map(([dept, scores]) => ({
      department: dept,
      average: (scores.reduce((sum, s) => sum + s, 0) / scores.length).toFixed(1),
    }));
  };

  // Get performance by manager
  const getPerformanceByManager = () => {
    const summary = getAllStaffSummary();
    const managerGroups = {};
    summary.forEach(person => {
      if (person.managerName && person.managerName !== '-') {
        if (!managerGroups[person.managerName]) {
          managerGroups[person.managerName] = [];
        }
        managerGroups[person.managerName].push(parseFloat(person.finalScore) || 0);
      }
    });
    
    return Object.entries(managerGroups).map(([manager, scores]) => ({
      manager,
      average: (scores.reduce((sum, s) => sum + s, 0) / scores.length).toFixed(1),
    }));
  };

  // Render All Staff tab
  const renderAllStaffTab = () => (
    <View style={styles.tigerTableContainer}>
      {/* Department Management */}
      <View style={styles.departmentManagerSection}>
        <Text style={[styles.departmentManagerTitle, { color: palette.text || 'white' }]}>Departments</Text>
        <View style={styles.departmentTagsRow}>
          {departments.map((dept, idx) => (
            <View key={idx} style={styles.departmentTagPill}>
              <Text style={styles.departmentTagPillText}>{dept}</Text>
            </View>
          ))}
        </View>
        <View style={styles.addDepartmentInputRow}>
          <TextInput
            style={[styles.addDepartmentTextInput, { color: '#000000', borderColor: '#000000' }]}
            placeholder="Add new department..."
            placeholderTextColor="rgba(0,0,0,0.4)"
            value={newDepartment}
            onChangeText={setNewDepartment}
            onSubmitEditing={addDepartment}
          />
          <TouchableOpacity style={styles.addDepartmentBtn} onPress={addDepartment}>
            <Text style={styles.addDepartmentBtnText}>+ Add</Text>
          </TouchableOpacity>
        </View>
      </View>

      <View style={styles.sectionHeader}>
        <Text style={[styles.sectionTitle, { color: palette.text || 'white' }]}>All Staff</Text>
        {!reviewMode && (
        <TouchableOpacity onPress={addStaff} style={[styles.addButton, { backgroundColor: palette.primary || '#FF0600' }]}>
          <Text style={styles.addButtonText}>+ Add Staff</Text>
        </TouchableOpacity>
        )}
      </View>
      <ScrollView horizontal style={styles.tigerHorizontalScroll} showsHorizontalScrollIndicator={true}>
        <View style={[styles.tigerMatrixContainer, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
          {/* Fixed Header Row */}
          <View style={styles.tigerFixedHeaderRow}>
            <View style={[styles.tigerPersonColumn, styles.tigerHeaderColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
              <View style={[styles.tigerHeader, { backgroundColor: 'rgba(255,255,255,0.05)' }]}>
                <Text style={[styles.tigerHeaderText, { color: palette.text || 'white' }]}>Person</Text>
              </View>
            </View>
            <View style={[styles.tigerTitleColumn, styles.tigerHeaderColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
              <View style={[styles.tigerHeader, { backgroundColor: 'rgba(255,255,255,0.05)' }]}>
                <Text style={[styles.tigerHeaderText, { color: palette.text || 'white' }]}>Title</Text>
              </View>
            </View>
            <View style={[styles.tigerDepartmentColumn, styles.tigerHeaderColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
              <View style={[styles.tigerHeader, { backgroundColor: 'rgba(255,255,255,0.05)' }]}>
                <Text style={[styles.tigerHeaderText, { color: palette.text || 'white' }]}>Department</Text>
              </View>
            </View>
            <View style={[styles.tigerReportsToColumn, styles.tigerHeaderColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
              <View style={[styles.tigerHeader, { backgroundColor: 'rgba(255,255,255,0.05)' }]}>
                <Text style={[styles.tigerHeaderText, { color: palette.text || 'white' }]}>Reports To</Text>
              </View>
            </View>
            <View style={[styles.tigerTypeColumn, styles.tigerHeaderColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
              <View style={[styles.tigerHeader, { backgroundColor: 'rgba(255,255,255,0.05)' }]}>
                <Text style={[styles.tigerHeaderText, { color: palette.text || 'white' }]}>Type</Text>
              </View>
            </View>
            <View style={[styles.tigerActiveColumn, styles.tigerHeaderColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
              <View style={[styles.tigerHeader, { backgroundColor: 'rgba(255,255,255,0.05)' }]}>
                <Text style={[styles.tigerHeaderText, { color: palette.text || 'white' }]}>Active</Text>
              </View>
            </View>
            <View style={[styles.tigerFLSAColumn, styles.tigerHeaderColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
              <View style={[styles.tigerHeader, { backgroundColor: 'rgba(255,255,255,0.05)' }]}>
                <Text style={[styles.tigerHeaderText, { color: palette.text || 'white' }]}>FLSA Status</Text>
              </View>
            </View>
            {!reviewMode && (
            <View style={[styles.tigerActionColumn, styles.tigerHeaderColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
              <View style={[styles.tigerHeader, { backgroundColor: 'rgba(255,255,255,0.05)' }]}>
                <Text style={[styles.tigerHeaderText, { color: palette.text || 'white' }]}>Action</Text>
              </View>
            </View>
            )}
          </View>

          {/* Scrollable Content Rows */}
          <ScrollView 
            style={styles.tigerVerticalScrollContent} 
            showsVerticalScrollIndicator={true}
            nestedScrollEnabled={true}
          >
            <View style={styles.tigerContentRowsContainer}>
              {/* Person Column */}
              <View style={[styles.tigerPersonColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                {allStaff.map((staff, idx) => (
                  <View key={idx} style={[styles.tigerRow, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                    <TextInput
                      style={[styles.tigerCellInput, { color: palette.text || 'white', borderColor: palette.border || 'rgba(255,255,255,0.2)' }]}
                      value={staff.person}
                      editable={!reviewMode}
                      onChangeText={(value) => {
                        const newStaff = [...allStaff];
                        newStaff[idx] = { ...newStaff[idx], person: value };
                        setAllStaff(newStaff);
                      }}
                      placeholder="Person"
                      placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.4)'}
                    />
                  </View>
                ))}
              </View>

              {/* Title Column */}
              <View style={[styles.tigerTitleColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                {allStaff.map((staff, idx) => (
                  <View key={idx} style={[styles.tigerRow, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                    <TextInput
                      style={[styles.tigerCellInput, { color: palette.text || 'white', borderColor: palette.border || 'rgba(255,255,255,0.2)' }]}
                      value={staff.title}
                      editable={!reviewMode}
                      onChangeText={(value) => {
                        const newStaff = [...allStaff];
                        newStaff[idx] = { ...newStaff[idx], title: value };
                        setAllStaff(newStaff);
                      }}
                      placeholder="Title"
                      placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.4)'}
                    />
                  </View>
                ))}
              </View>

              {/* Department Column */}
              <View style={[styles.tigerDepartmentColumn, { zIndex: 15, borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                {allStaff.map((staff, idx) => (
                    <Dropdown
                      value={staff.department}
                      key={idx}
                      options={departments.map(d => ({ value: d, label: d }))}
                      onSelect={(value) => {
                        const newStaff = [...allStaff];
                        newStaff[idx] = { ...newStaff[idx], department: value };
                        setAllStaff(newStaff);
                      }}
                      palette={palette}
                      style={[styles.tigerRow, { width: '100%' }]}
                    />
                ))}
              </View>

              {/* Reports To Column - Now a Dropdown */}
              <View style={[styles.tigerReportsToColumn, { zIndex: 10, borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                {allStaff.map((staff, idx) => (
                    <Dropdown
                      value={staff.reportsTo}
                      key={idx}
                      options={getStaffNames()}
                      onSelect={(value) => {
                        const newStaff = [...allStaff];
                        newStaff[idx] = { ...newStaff[idx], reportsTo: value };
                        setAllStaff(newStaff);
                      }}
                      palette={palette}
                      style={[styles.tigerRow, { width: '100%' }]}
                      placeholder="Select Manager..."
                    />
                ))}
              </View>

              {/* Type Column */}
              <View style={[styles.tigerTypeColumn, { zIndex: 25, borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                {allStaff.map((staff, idx) => (
                    <Dropdown
                      value={staff.type}
                      key={idx}
                      disabled={reviewMode}
                      options={[
                        { value: 'FTE', label: 'FTE' },
                        { value: 'Part Time', label: 'Part Time' },
                        { value: 'Consultant', label: 'Consultant' },
                      ]}
                      onSelect={(value) => {
                        const newStaff = [...allStaff];
                        newStaff[idx] = { ...newStaff[idx], type: value };
                        setAllStaff(newStaff);
                      }}
                      palette={palette}
                      style={styles.tigerRow}
                    />
                ))}
              </View>

              {/* Active Column */}
              <View style={[styles.tigerActiveColumn, { zIndex: 20, borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                {allStaff.map((staff, idx) => (
                  <Dropdown
                      key={idx}
                      disabled={reviewMode}
                      value={staff.active}
                      options={[
                        { value: 'Y', label: 'Y' },
                        { value: 'N', label: 'N' },
                      ]}
                      onSelect={(value) => {
                        const newStaff = [...allStaff];
                        newStaff[idx] = { ...newStaff[idx], active: value };
                        setAllStaff(newStaff);
                      }}
                      palette={palette}
                      style={styles.tigerRow}
                    />
                ))}
              </View>

              {/* FLSA Status Column */}
              <View style={[styles.tigerFLSAColumn, { zIndex: 15, borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                {allStaff.map((staff, idx) => (
                  <Dropdown
                      disabled={reviewMode}
                      value={staff.flsaStatus}
                      key={idx}
                      options={[
                        { value: 'Exempt', label: 'Exempt' },
                        { value: 'Non-Exempt', label: 'Non-Exempt' },
                      ]}
                      onSelect={(value) => {
                        const newStaff = [...allStaff];
                        newStaff[idx] = { ...newStaff[idx], flsaStatus: value };
                        setAllStaff(newStaff);
                      }}
                      palette={palette}
                      style={styles.tigerRow}
                  />
                ))}
              </View>

              {/* Action Column */}
              {!reviewMode && (
              <View style={[styles.tigerActionColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                {allStaff.map((staff, idx) => (
                  <View key={idx} style={[styles.tigerRow, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                    {allStaff.length > 1 && (
                      <TouchableOpacity onPress={() => removeStaff(idx)} style={styles.tigerRemoveButton}>
                        <Text style={[styles.removeButtonText, { color: '#EA806B' }]}>✕</Text>
                      </TouchableOpacity>
                    )}
                  </View>
                ))}
              </View>
              )}
            </View>
          </ScrollView>
        </View>
      </ScrollView>
    </View>
  );

  // Render Department tab - shows staff filtered by department with metrics
  const renderDepartmentTab = (departmentName, departmentData, setDepartmentData, readOnly = false) => (
    <View style={styles.tigerTableContainer}>
      <ScrollView horizontal style={styles.tigerHorizontalScroll} showsHorizontalScrollIndicator={true}>
        <View style={[styles.tigerMatrixContainer, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
          {/* Fixed Header Row */}
          <View style={styles.tigerFixedHeaderRow}>
            <View style={[styles.tigerDeptEmployeeColumn, styles.tigerHeaderColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
              <View style={[styles.tigerHeader, { backgroundColor: 'rgba(255,255,255,0.05)' }]}>
                <Text style={[styles.tigerHeaderText, { color: palette.text || 'white' }]}>Employee Name</Text>
              </View>
            </View>
            <View style={[styles.tigerDeptTitleColumn, styles.tigerHeaderColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
              <View style={[styles.tigerHeader, { backgroundColor: 'rgba(255,255,255,0.05)' }]}>
                <Text style={[styles.tigerHeaderText, { color: palette.text || 'white' }]}>Title</Text>
              </View>
            </View>
            <View style={[styles.tigerDeptDepartmentColumn, styles.tigerHeaderColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
              <View style={[styles.tigerHeader, { backgroundColor: 'rgba(255,255,255,0.05)' }]}>
                <Text style={[styles.tigerHeaderText, { color: palette.text || 'white' }]}>Department</Text>
              </View>
            </View>
            <View style={[styles.tigerDeptReportsToColumn, styles.tigerHeaderColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
              <View style={[styles.tigerHeader, { backgroundColor: 'rgba(255,255,255,0.05)' }]}>
                <Text style={[styles.tigerHeaderText, { color: palette.text || 'white' }]}>Reports To</Text>
              </View>
            </View>
            <View style={[styles.tigerDeptTypeColumn, styles.tigerHeaderColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
              <View style={[styles.tigerHeader, { backgroundColor: 'rgba(255,255,255,0.05)' }]}>
                <Text style={[styles.tigerHeaderText, { color: palette.text || 'white' }]}>Type</Text>
              </View>
            </View>
            <View style={[styles.tigerDeptActiveColumn, styles.tigerHeaderColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
              <View style={[styles.tigerHeader, { backgroundColor: 'rgba(255,255,255,0.05)' }]}>
                <Text style={[styles.tigerHeaderText, { color: palette.text || 'white' }]}>Active</Text>
              </View>
            </View>
            <View style={[styles.tigerDeptQ1MeasureColumn, styles.tigerHeaderColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
              <View style={[styles.tigerHeader, { backgroundColor: 'rgba(255,255,255,0.05)' }]}>
                <Text style={[styles.tigerHeaderText, { color: palette.text || 'white' }]}>Q1 Measure</Text>
              </View>
            </View>
            <View style={[styles.tigerDeptQ1ScoreColumn, styles.tigerHeaderColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
              <View style={[styles.tigerHeader, { backgroundColor: 'rgba(255,255,255,0.05)' }]}>
                <Text style={[styles.tigerHeaderText, { color: palette.text || 'white' }]}>Q1 Score (%)</Text>
              </View>
            </View>
            <View style={[styles.tigerDeptQ1WeightColumn, styles.tigerHeaderColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
              <View style={[styles.tigerHeader, { backgroundColor: 'rgba(255,255,255,0.05)' }]}>
                <Text style={[styles.tigerHeaderText, { color: palette.text || 'white' }]}>Q1 Weight</Text>
              </View>
            </View>
            <View style={[styles.tigerDeptQ2MeasureColumn, styles.tigerHeaderColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
              <View style={[styles.tigerHeader, { backgroundColor: 'rgba(255,255,255,0.05)' }]}>
                <Text style={[styles.tigerHeaderText, { color: palette.text || 'white' }]}>Q2 Measure</Text>
              </View>
            </View>
            <View style={[styles.tigerDeptQ2ScoreColumn, styles.tigerHeaderColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
              <View style={[styles.tigerHeader, { backgroundColor: 'rgba(255,255,255,0.05)' }]}>
                <Text style={[styles.tigerHeaderText, { color: palette.text || 'white' }]}>Q2 Score (%)</Text>
              </View>
            </View>
            <View style={[styles.tigerDeptQ2WeightColumn, styles.tigerHeaderColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
              <View style={[styles.tigerHeader, { backgroundColor: 'rgba(255,255,255,0.05)' }]}>
                <Text style={[styles.tigerHeaderText, { color: palette.text || 'white' }]}>Q2 Weight</Text>
              </View>
            </View>
            <View style={[styles.tigerDeptQ3MeasureColumn, styles.tigerHeaderColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
              <View style={[styles.tigerHeader, { backgroundColor: 'rgba(255,255,255,0.05)' }]}>
                <Text style={[styles.tigerHeaderText, { color: palette.text || 'white' }]}>Q3 Measure</Text>
              </View>
            </View>
            <View style={[styles.tigerDeptQ3ScoreColumn, styles.tigerHeaderColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
              <View style={[styles.tigerHeader, { backgroundColor: 'rgba(255,255,255,0.05)' }]}>
                <Text style={[styles.tigerHeaderText, { color: palette.text || 'white' }]}>Q3 Score (%)</Text>
              </View>
            </View>
            <View style={[styles.tigerDeptQ3WeightColumn, styles.tigerHeaderColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
              <View style={[styles.tigerHeader, { backgroundColor: 'rgba(255,255,255,0.05)' }]}>
                <Text style={[styles.tigerHeaderText, { color: palette.text || 'white' }]}>Q3 Weight</Text>
              </View>
            </View>
            
            <View style={[styles.tigerDeptIndividualScoreColumn, styles.tigerHeaderColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
              <View style={[styles.tigerHeader, { backgroundColor: 'rgba(255,255,255,0.05)' }]}>
                <Text style={[styles.tigerHeaderText, { color: palette.text || 'white' }]}>Individual Score</Text>
              </View>
            </View>
            <View style={[styles.tigerDeptDirectReportsColumn, styles.tigerHeaderColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
              <View style={[styles.tigerHeader, { backgroundColor: 'rgba(255,255,255,0.05)' }]}>
                <Text style={[styles.tigerHeaderText, { color: palette.text || 'white' }]}>Direct Reports Avg</Text>
              </View>
            </View>
            <View style={[styles.tigerDeptFinalScoreColumn, styles.tigerHeaderColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
              <View style={[styles.tigerHeader, { backgroundColor: 'rgba(255,255,255,0.05)' }]}>
                <Text style={[styles.tigerHeaderText, { color: palette.text || 'white' }]}>Final Score</Text>
              </View>
            </View>
          </View>

          {/* Scrollable Content Rows */}
          <ScrollView 
            style={styles.tigerVerticalScrollContent} 
            showsVerticalScrollIndicator={true}
            nestedScrollEnabled={true}
          >
            <View style={styles.tigerContentRowsContainer}>
              {/* Employee Name Column */}
              <View style={[styles.tigerDeptEmployeeColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                {departmentData.map((person, idx) => (
                  <View key={idx} style={[styles.tigerRow, styles.attributeNameRow, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                    <Text style={[styles.tigerCellText, { color: palette.text || 'white' }]}>{person.person}</Text>
                  </View>
                ))}
              </View>

              {/* Title Column */}
              <View style={[styles.tigerDeptTitleColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                {departmentData.map((person, idx) => (
                  <View key={idx} style={[styles.tigerRow, styles.attributeNameRow, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                    <Text style={[styles.tigerCellText, { color: palette.text || 'white' }]}>{person.title}</Text>
                  </View>
                ))}
              </View>

              {/* Department Column */}
              <View style={[styles.tigerDeptDepartmentColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                {departmentData.map((person, idx) => (
                  <View key={idx} style={[styles.tigerRow, styles.attributeNameRow, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                    <Text style={[styles.tigerCellText, { color: palette.text || 'white' }]}>{person.department}</Text>
                  </View>
                ))}
              </View>

              {/* Reports To Column */}
              <View style={[styles.tigerDeptReportsToColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                {departmentData.map((person, idx) => (
                  <View key={idx} style={[styles.tigerRow, styles.attributeNameRow, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                    <Text style={[styles.tigerCellText, { color: palette.text || 'white' }]}>{person.reportsTo}</Text>
                  </View>
                ))}
              </View>

              {/* Type Column */}
              <View style={[styles.tigerDeptTypeColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                {departmentData.map((person, idx) => (
                  <View key={idx} style={[styles.tigerRow, styles.attributeNameRow, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                    <Text style={[styles.tigerCellText, { color: palette.text || 'white' }]}>{person.type}</Text>
                  </View>
                ))}
              </View>

              {/* Active Column */}
              <View style={[styles.tigerDeptActiveColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                {departmentData.map((person, idx) => (
                  <View key={idx} style={[styles.tigerRow, styles.attributeNameRow, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                    <Text style={[styles.tigerCellText, { color: palette.text || 'white' }]}>{person.active}</Text>
                  </View>
                ))}
              </View>

              {/* Q1 Measure Column */}
              <View style={[styles.tigerDeptQ1MeasureColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                {departmentData.map((person, idx) => (
                  <View key={idx} style={[styles.tigerRow, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                    <TextInput
                      style={[styles.tigerCellInput, { color: palette.text || 'white', borderColor: palette.border || 'rgba(255,255,255,0.2)' }]}
                      value={person.q1Measure}
                      editable={!reviewMode}
                      onChangeText={(value) => {
                        const newData = [...departmentData];
                        newData[idx] = { ...newData[idx], q1Measure: value };
                        setDepartmentData(newData);
                      }}
                      placeholder="Q1 Measure"
                      placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.4)'}
                    />
                  </View>
                ))}
              </View>

              {/* Q1 Score Column */}
              <View style={[styles.tigerDeptQ1ScoreColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                {departmentData.map((person, idx) => (
                  <View key={idx} style={[styles.tigerRow, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                    <TextInput
                      style={[styles.tigerCellInput, { color: palette.text || 'white', borderColor: palette.border || 'rgba(255,255,255,0.2)' }]}
                      value={person.q1Score}
                      editable={!reviewMode}
                      onChangeText={(value) => {
                        const newData = [...departmentData];
                        newData[idx] = { ...newData[idx], q1Score: value };
                        setDepartmentData(newData);
                      }}
                      placeholder="0-100"
                      keyboardType="numeric"
                      placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.4)'}
                    />
                  </View>
                ))}
              </View>

              {/* Q1 Weight Column */}
              <View style={[styles.tigerDeptQ1WeightColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                {departmentData.map((person, idx) => (
                  <View key={idx} style={[styles.tigerRow, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                    <TextInput
                      style={[styles.tigerCellInput, { color: palette.text || 'white', borderColor: palette.border || 'rgba(255,255,255,0.2)' }]}
                      value={person.q1Weight}
                      editable={!reviewMode}
                      onChangeText={(value) => {
                        const newData = [...departmentData];
                        newData[idx] = { ...newData[idx], q1Weight: value };
                        setDepartmentData(newData);
                      }}
                      placeholder="Weight"
                      keyboardType="numeric"
                      placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.4)'}
                    />
                  </View>
                ))}
              </View>

              {/* Q2 Measure Column */}
              <View style={[styles.tigerDeptQ2MeasureColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                {departmentData.map((person, idx) => (
                  <View key={idx} style={[styles.tigerRow, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                    <TextInput
                      style={[styles.tigerCellInput, { color: palette.text || 'white', borderColor: palette.border || 'rgba(255,255,255,0.2)' }]}
                      value={person.q2Measure}
                      editable={!reviewMode}
                      onChangeText={(value) => {
                        const newData = [...departmentData];
                        newData[idx] = { ...newData[idx], q2Measure: value };
                        setDepartmentData(newData);
                      }}
                      placeholder="Q2 Measure"
                      placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.4)'}
                    />
                  </View>
                ))}
              </View>

              {/* Q2 Score Column */}
              <View style={[styles.tigerDeptQ2ScoreColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                {departmentData.map((person, idx) => (
                  <View key={idx} style={[styles.tigerRow, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                    <TextInput
                      style={[styles.tigerCellInput, { color: palette.text || 'white', borderColor: palette.border || 'rgba(255,255,255,0.2)' }]}
                      value={person.q2Score}
                      editable={!reviewMode}
                      onChangeText={(value) => {
                        const newData = [...departmentData];
                        newData[idx] = { ...newData[idx], q2Score: value };
                        setDepartmentData(newData);
                      }}
                      placeholder="0-100"
                      keyboardType="numeric"
                      placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.4)'}
                    />
                  </View>
                ))}
              </View>

              {/* Q2 Weight Column */}
              <View style={[styles.tigerDeptQ2WeightColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                {departmentData.map((person, idx) => (
                  <View key={idx} style={[styles.tigerRow, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                    <TextInput
                      style={[styles.tigerCellInput, { color: palette.text || 'white', borderColor: palette.border || 'rgba(255,255,255,0.2)' }]}
                      value={person.q2Weight}
                      editable={!reviewMode}
                      onChangeText={(value) => {
                        const newData = [...departmentData];
                        newData[idx] = { ...newData[idx], q2Weight: value };
                        setDepartmentData(newData);
                      }}
                      placeholder="Weight"
                      keyboardType="numeric"
                      placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.4)'}
                    />
                  </View>
                ))}
              </View>

              {/* Q3 Measure Column */}
              <View style={[styles.tigerDeptQ3MeasureColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                {departmentData.map((person, idx) => (
                  <View key={idx} style={[styles.tigerRow, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                    <TextInput
                      style={[styles.tigerCellInput, { color: palette.text || 'white', borderColor: palette.border || 'rgba(255,255,255,0.2)' }]}
                      value={person.q3Measure}
                      editable={!reviewMode}
                      onChangeText={(value) => {
                        const newData = [...departmentData];
                        newData[idx] = { ...newData[idx], q3Measure: value };
                        setDepartmentData(newData);
                      }}
                      placeholder="Q3 Measure"
                      placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.4)'}
                    />
                  </View>
                ))}
              </View>

              {/* Q3 Score Column */}
              <View style={[styles.tigerDeptQ3ScoreColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                {departmentData.map((person, idx) => (
                  <View key={idx} style={[styles.tigerRow, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                    <TextInput
                      style={[styles.tigerCellInput, { color: palette.text || 'white', borderColor: palette.border || 'rgba(255,255,255,0.2)' }]}
                      value={person.q3Score}
                      editable={!reviewMode}
                      onChangeText={(value) => {
                        const newData = [...departmentData];
                        newData[idx] = { ...newData[idx], q3Score: value };
                        setDepartmentData(newData);
                      }}
                      placeholder="0-100"
                      keyboardType="numeric"
                      placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.4)'}
                    />
                  </View>
                ))}
              </View>

              {/* Q3 Weight Column */}
              <View style={[styles.tigerDeptQ3WeightColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                {departmentData.map((person, idx) => (
                  <View key={idx} style={[styles.tigerRow, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                    <TextInput
                      style={[styles.tigerCellInput, { color: palette.text || 'white', borderColor: palette.border || 'rgba(255,255,255,0.2)' }]}
                      value={person.q3Weight}
                      editable={!reviewMode}
                      onChangeText={(value) => {
                        const newData = [...departmentData];
                        newData[idx] = { ...newData[idx], q3Weight: value };
                        setDepartmentData(newData);
                      }}
                      placeholder="Weight"
                      keyboardType="numeric"
                      placeholderTextColor={palette.mutedText || 'rgba(255,255,255,0.4)'}
                    />
                  </View>
                ))}
              </View>


              {/* Individual Score Column */}
              <View style={[styles.tigerDeptIndividualScoreColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                {departmentData.map((person, idx) => {
                  const individualScore = calculateIndividualScore(person);
                  return (
                    <View key={idx} style={[styles.tigerRow, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                      <Text style={[styles.tigerCellText, { color: palette.primary || '#FF0600', fontWeight: '700' }]}>
                        {individualScore ? `${individualScore}%` : '-'}
                      </Text>
                    </View>
                  );
                })}
              </View>

              {/* Direct Reports Avg Column */}
              <View style={[styles.tigerDeptDirectReportsColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                {departmentData.map((person, idx) => {
                  const directReportsAvg = calculateDirectReportsAvg(person.person, departmentData);
                  return (
                    <View key={idx} style={[styles.tigerRow, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                      <Text style={[styles.tigerCellText, { color: palette.primary || '#FF0600', fontWeight: '700' }]}>
                        {directReportsAvg ? `${directReportsAvg}%` : '-'}
                      </Text>
                    </View>
                  );
                })}
              </View>

              {/* Final Score Column */}
              <View style={[styles.tigerDeptFinalScoreColumn, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                {departmentData.map((person, idx) => {
                  const finalScore = calculateFinalScore(person, departmentData);
                  return (
                    <View key={idx} style={[styles.tigerRow, { borderColor: palette.border || 'rgba(255,255,255,0.1)' }]}>
                      <Text style={[styles.tigerCellText, { color: palette.primary || '#FF0600', fontWeight: '700' }]}>
                        {finalScore ? `${finalScore}%` : '-'}
                      </Text>
                    </View>
                  );
                })}
              </View>
            </View>
          </ScrollView>
        </View>
      </ScrollView>
    </View>
  );

  // Render Summary tab
  const renderSummaryTab = () => {
    const allStaffSummary = getAllStaffSummary();
    const deptPerformance = getPerformanceByDepartment();
    const managerPerformance = getPerformanceByManager();

    return (
      <ScrollView style={styles.tabContent}>
        <View style={styles.summarySection}>
          <Text style={[styles.summaryTitle, { color: palette.text || 'white' }]}>All Staff Summary</Text>
          <View style={styles.summaryTable}>
            <View style={styles.summaryHeader}>
              <Text style={[styles.summaryHeaderText, { color: palette.text || 'white' }]}>Department</Text>
              <Text style={[styles.summaryHeaderText, { color: palette.text || 'white' }]}>Employee Name</Text>
              <Text style={[styles.summaryHeaderText, { color: palette.text || 'white' }]}>Title</Text>
              <Text style={[styles.summaryHeaderText, { color: palette.text || 'white' }]}>Manager Name</Text>
              <Text style={[styles.summaryHeaderText, { color: palette.text || 'white' }]}>Final Score</Text>
            </View>
            {allStaffSummary.map((person, idx) => (
              <View key={idx} style={styles.summaryRow}>
                <Text style={[styles.summaryCell, { color: palette.text || 'white' }]}>{person.department}</Text>
                <Text style={[styles.summaryCell, { color: palette.text || 'white' }]}>{person.employeeName}</Text>
                <Text style={[styles.summaryCell, { color: palette.text || 'white' }]}>{person.title}</Text>
                <Text style={[styles.summaryCell, { color: palette.text || 'white' }]}>{person.managerName}</Text>
                <Text style={[styles.summaryCell, { color: palette.primary || '#FF0600', fontWeight: '700' }]}>
                  {person.finalScore}%
                </Text>
              </View>
            ))}
          </View>
        </View>

        <View style={styles.summarySection}>
          <Text style={[styles.summaryTitle, { color: palette.text || 'white' }]}>Performance by Department</Text>
          <View style={styles.summaryTable}>
            <View style={styles.summaryHeader}>
              <Text style={[styles.summaryHeaderText, { color: palette.text || 'white' }]}>Department</Text>
              <Text style={[styles.summaryHeaderText, { color: palette.text || 'white' }]}>Average Score</Text>
            </View>
            {deptPerformance.map((dept, idx) => (
              <View key={idx} style={styles.summaryRow}>
                <Text style={[styles.summaryCell, { color: palette.text || 'white' }]}>{dept.department}</Text>
                <Text style={[styles.summaryCell, { color: palette.primary || '#FF0600', fontWeight: '700' }]}>
                  {dept.average}%
                </Text>
              </View>
            ))}
          </View>
        </View>

        <View style={styles.summarySection}>
          <Text style={[styles.summaryTitle, { color: palette.text || 'white' }]}>Performance by Manager</Text>
          <View style={styles.summaryTable}>
            <View style={styles.summaryHeader}>
              <Text style={[styles.summaryHeaderText, { color: palette.text || 'white' }]}>Manager</Text>
              <Text style={[styles.summaryHeaderText, { color: palette.text || 'white' }]}>Average Score (Direct Reports)</Text>
            </View>
            {managerPerformance.map((mgr, idx) => (
              <View key={idx} style={styles.summaryRow}>
                <Text style={[styles.summaryCell, { color: palette.text || 'white' }]}>{mgr.manager}</Text>
                <Text style={[styles.summaryCell, { color: palette.primary || '#FF0600', fontWeight: '700' }]}>
                  {mgr.average}%
                </Text>
              </View>
            ))}
          </View>
        </View>
      </ScrollView>
    );
  };

  // Generate tabs dynamically based on departments from data
  const tabs = [
    { id: 'all_staff', label: 'All Staff' },
    ...departments.map(dept => ({ id: dept.toLowerCase().replace(/\s+/g, '_'), label: dept })),
    { id: 'summary', label: 'Summary' },
  ];

  return (
    <View style={[styles.widgetCard, { backgroundColor: palette.surface, borderColor: palette.border || 'rgba(255,255,255,0.16)' }]}>
      <View style={styles.widgetHeaderContainer}>
        <View style={styles.widgetHeader}>
          <Text style={[styles.widgetTitle, { color: palette.text || 'white' }]}>
            🐅 {widget.title || 'Tiger Team Breakouts Tracker'}
          </Text>
          {widget.description && (
            <Text style={[styles.widgetDescription, { color: palette.mutedText || 'rgba(255,255,255,0.7)' }]}>
              {widget.description}
            </Text>
          )}
        </View>
      </View>

      {/* Tab Navigation */}
      <View style={styles.tabNavigation}>
        {tabs.map(tab => (
          <TouchableOpacity
            key={tab.id}
            onPress={() => setActiveTab(tab.id)}
            style={[
              styles.tabButton,
              activeTab === tab.id && { backgroundColor: palette.primary || '#FF0600' },
              activeTab !== tab.id && { backgroundColor: 'rgba(255,255,255,0.1)' },
            ]}
          >
            <Text style={[
              styles.tabButtonText,
              { color: activeTab === tab.id ? 'white' : palette.text || 'white' }
            ]}>
              {tab.label}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      {/* Tab Content */}
      {activeTab === 'all_staff' && renderAllStaffTab()}
      {activeTab === 'summary' && renderSummaryTab()}
      {/* Render department tabs dynamically */}
      {departments.map(dept => {
        const tabId = dept.toLowerCase().replace(/\s+/g, '_');
        if (activeTab === tabId) {
          const deptData = allStaff.filter(s => s.department === dept && s.active === 'Y');
          return renderDepartmentTab(dept, deptData, null, true);
        }
        return null;
      })}

      {/* Complete Button */}
      {!reviewMode && (
        <TouchableOpacity 
          onPress={handleComplete} 
          style={[styles.saveButton, { backgroundColor: palette.primary || '#FF0600' }]}
        >
          <Text style={styles.saveButtonText}>Complete</Text>
        </TouchableOpacity>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  widgetCard: {
    borderRadius: 16,
    padding: 20,
    marginBottom: 16,
    borderWidth: 3,
    borderColor: '#000000',
    backgroundColor: 'white',
    height: '100%',
    width: '100%',
    maxWidth: 1440,
    ...Platform.select({
      web: {
        boxShadow: '5px 7px 0px rgba(0,0,0,0.85)',
      },
    }),
  },
  widgetHeader: {
    marginBottom: 16,
    flex: 1,
  },
  widgetHeaderContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
  },
  widgetTitle: {
    fontSize: 20,
    fontFamily: 'Chivo',
    fontWeight: '900',
    marginBottom: 8,
  },
  widgetDescription: {
    fontSize: 14,
    fontFamily: 'Archivo',
    lineHeight: 20,
  },
  content: {
    width: '100%',
  },
  section: {
    marginBottom: 24,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 18,
    fontFamily: 'Chivo',
    fontWeight: '700',
  },
  addButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: '#000000',
    ...Platform.select({
      web: {
        boxShadow: '2px 3px 0px rgba(0,0,0,0.8)',
        cursor: 'pointer',
      },
    }),
  },
  addButtonText: {
    color: '#fff',
    fontSize: 13,
    fontFamily: 'Archivo',
    fontWeight: '700',
  },
  input: {
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    fontSize: 14,
    fontFamily: 'Archivo',
  },
  textArea: {
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    fontSize: 14,
    fontFamily: 'Archivo',
    minHeight: 80,
    textAlignVertical: 'top',
    marginTop: 8,
  },
  removeButton: {
    padding: 4,
  },
  removeButtonText: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  saveButton: {
    padding: 16,
    borderRadius: 12,
    alignItems: 'center',
    marginTop: 16,
    borderWidth: 3,
    borderColor: '#000000',
    ...Platform.select({
      web: {
        boxShadow: '4px 5px 0px rgba(0,0,0,0.85)',
        cursor: 'pointer',
      },
    }),
  },
  saveButtonText: {
    color: '#fff',
    fontSize: 16,
    fontFamily: 'Chivo',
    fontWeight: '700',
  },
  showResultsButton: {
    padding: 16,
    borderRadius: 12,
    alignItems: 'center',
    marginTop: 16,
    zIndex: 1,
    borderWidth: 3,
    borderColor: '#000000',
    ...Platform.select({
      web: {
        boxShadow: '4px 5px 0px rgba(0,0,0,0.85)',
        cursor: 'pointer',
      },
    }),
  },
  showResultsButtonText: {
    color: '#fff',
    fontSize: 16,
    fontFamily: 'Chivo',
    fontWeight: '700',
  },
  resultsViewContainer: {
    flex: 1,
    minHeight: 400,
    backgroundColor: 'white',
  },
  resultsHeader: {
    padding: 20,
    alignItems: 'center',
    borderBottomWidth: 3,
    borderBottomColor: '#000000',
    backgroundColor: '#FF4747',
  },
  resultsViewTitle: {
    fontSize: 28,
    fontFamily: 'Chivo',
    fontWeight: '700',
    marginBottom: 8,
  },
  resultsViewSubtitle: {
    fontSize: 16,
    fontFamily: 'Archivo',
  },
  resultsScrollView: {
    flex: 1,
    padding: 16,
  },
  resultCard: {
    padding: 20,
    borderRadius: 14,
    marginBottom: 16,
    borderWidth: 3,
    borderColor: '#000000',
    backgroundColor: 'white',
    ...Platform.select({
      web: {
        boxShadow: '3px 4px 0px rgba(0,0,0,0.8)',
      },
    }),
  },
  resultCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  resultRankContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  resultRankIcon: {
    fontSize: 32,
  },
  resultRankText: {
    fontSize: 18,
    fontFamily: 'Chivo',
    fontWeight: '700',
  },
  resultScoreBadge: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
  },
  resultScoreBadgeText: {
    color: '#fff',
    fontSize: 14,
    fontFamily: 'Chivo',
    fontWeight: '700',
  },
  resultCardTitle: {
    fontSize: 18,
    fontFamily: 'Archivo',
    fontWeight: '600',
    lineHeight: 24,
  },
  resultsActions: {
    flexDirection: 'row',
    gap: 12,
    padding: 16,
    borderTopWidth: 3,
    borderTopColor: '#000000',
    backgroundColor: 'rgba(0,0,0,0.03)',
  },
  resultsButton: {
    flex: 1,
    padding: 16,
    borderRadius: 12,
    alignItems: 'center',
    borderWidth: 3,
    borderColor: '#000000',
    ...Platform.select({
      web: {
        boxShadow: '3px 4px 0px rgba(0,0,0,0.8)',
        cursor: 'pointer',
      },
    }),
  },
  goBackButton: {
    // Styles applied inline
  },
  completeButton: {
    // Styles applied inline
  },
  resultsButtonText: {
    color: '#fff',
    fontSize: 16,
    fontFamily: 'Chivo',
    fontWeight: '700',
  },
  // Strategic Decision Matrix styles
  decisionMatrixContainer: {
    flex: 1,
    minHeight: 500,
    marginBottom: 16,
    borderWidth: 3,
    borderColor: '#000000',
    borderRadius: 14,
    backgroundColor: 'white',
    ...Platform.select({
      web: {
        boxShadow: '4px 5px 0px rgba(0,0,0,0.85)',
      },
    }),
  },
  decisionMatrixHorizontalScroll: {
    flex: 1,
  },
  verticalScrollWrapper: {
    flex: 1,
  },
  matrixRowContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  fixedLeftSection: {
    flexDirection: 'row',
    zIndex: 10,
    backgroundColor: 'rgba(0,0,0,0.2)',
  },
  horizontalScrollSection: {
    flex: 1,
  },
  initiativesContainer: {
    flexDirection: 'row',
  },
  tableContainer: {
    flex: 1,
    minHeight: 200,
    marginBottom: 16,
    borderWidth: 3,
    borderColor: '#000000',
    borderRadius: 14,
    overflow: 'hidden',
    backgroundColor: 'white',
    ...Platform.select({
      web: {
        boxShadow: '4px 5px 0px rgba(0,0,0,0.85)',
      },
    }),
  },
  // Strategic Action Matrix - no horizontal scroll, fit to container
  actionMatrixTableContainer: {
    flex: 1,
    minHeight: 200,
    marginBottom: 16,
    borderWidth: 3,
    borderColor: '#000000',
    borderRadius: 14,
    backgroundColor: 'white',
    ...Platform.select({
      web: {
        boxShadow: '4px 5px 0px rgba(0,0,0,0.85)',
      },
    }),
  },
  actionMatrixContent: {
    flexDirection: 'column',
    flex: 1,
  },
  horizontalScroll: {
    flex: 1,
  },
  matrixContainer: {
    flexDirection: 'column',
    flex: 1,
  },
  fixedHeaderRow: {
    flexDirection: 'row',
    zIndex: 100,
    borderBottomWidth: 3,
    borderBottomColor: '#000000',
    backgroundColor: '#FF4747',
  },
  headerColumn: {
    borderBottomWidth: 0,
  },
  verticalScrollContent: {
    flex: 1,
    paddingBottom: 200,
    ...(Platform.OS === 'web' && {
      overflow: 'visible',
    }),
  },
  contentRowsContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    ...(Platform.OS === 'web' && {
      overflow: 'visible',
    }),
  },
  taskColumn: {
    flex: 1.5,
    minWidth: 140,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.1)',
  },
  quadrantColumn: {
    flex: 1,
    minWidth: 100,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.1)',
  },
  taskNameColumn: {
    flex: 2,
    minWidth: 160,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.1)',
  },
  urgencyScoreColumn: {
    flex: 0.5,
    minWidth: 60,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.1)',
  },
  fixedAttributeColumn: {
    width: 500,
    borderWidth: 1,
    borderRightWidth: 1,
  },
  fixedDescriptionColumn: {
    width: 300,
    borderWidth: 1,
    borderLeftWidth: 0,
    borderRightWidth: 0,
  },
  fixedWeightColumn: {
    width: 100,
    borderWidth: 1,
    borderLeftWidth: 0,
    borderRightWidth: 1,
  },
  weightRow: {
    padding: 12,
    borderBottomWidth: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  weightInput: {
    padding: 8,
    borderRadius: 4,
    borderWidth: 1,
    fontSize: 16,
    fontFamily: 'Chivo',
    fontWeight: '700',
    textAlign: 'center',
    width: 50,
    marginBottom: 4,
  },
  weightHint: {
    fontSize: 9,
    fontFamily: 'Archivo',
    textAlign: 'center',
  },
  attributeHeader: {
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.1)',
    justifyContent: 'center',
  },
  taskHeader: {
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.1)',
    justifyContent: 'center',
    height: 60,
  },
  header: {
    height: 80,
  },
  attributeHeaderText: {
    fontSize: 14,
    fontFamily: 'Chivo',
    fontWeight: '700',
  },
  attributeNameRow: {
    padding: 12,
    borderBottomWidth: 1,
    gap: 4,
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  taskNameRow: {
    padding: 12,
    borderBottomWidth: 1,
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  row: {
    minHeight: 80,
    flex: 1,
  },
  taskRow: {
    height: 80,
  },
  attributeName: {
    fontSize: 13,
    fontFamily: 'Chivo',
    fontWeight: '700',
  },
  attributeDescriptionRow: {
    padding: 12,
    borderBottomWidth: 1,
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
  },
  attributeDefinition: {
    fontSize: 11,
    fontFamily: 'Archivo',
    lineHeight: 16,
  },
  totalRow: {
    padding: 12,
    borderTopWidth: 2,
    borderTopColor: 'rgba(255,255,255,0.2)',
    height: 50,
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  totalLabel: {
    fontSize: 14,
    fontFamily: 'Chivo',
    fontWeight: '700',
  },
  initiativeColumn: {
    width: 400,
    borderWidth: 1,
    borderLeftWidth: 0,
  },
  taskDropdownColumn: {
    width: 300,
    borderWidth: 1,
    borderLeftWidth: 0,
  },
  initiativeHeader: {
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.1)',
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
    flex: 1,
  },
  initiativeTitleInput: {
    fontSize: 12,
    fontFamily: 'Chivo',
    fontWeight: '700',
    flex: 1,
    padding: 4,
  },
  columnHeadersRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    paddingTop: 4,
    flex: 1,
  },
  initiativeRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    alignItems: 'stretch',
  },
  initiativeRankRowContainer: {
    display: 'flex',
    flexDirection: 'row',
  },
  initiativeRankRow: {
    display: 'flex',
    flexDirection: 'column',
  },
  initiativeCell: {
    flex: 1,
    height: 80,
    justifyContent: 'center',
    alignItems: 'center',
  },
  taskScoreCell: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  rankCell: {
    flex: 2,
  },
  scoreCell: {
    backgroundColor: 'rgba(255,255,255,0.03)',
    minWidth: 80,
    justifyContent: 'center',
    flex: 1,
  },
  rankDescription: {
    marginTop: 4,
  },
  rankDescriptionText: {
    fontSize: 10,
    fontFamily: 'Archivo',
  },
  initiativeTotalRow: {
    height: 50,
    padding: 12,
    borderTopWidth: 2,
    borderTopColor: 'rgba(255,255,255,0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  totalScoreText: {
    fontSize: 18,
    fontFamily: 'Chivo',
    fontWeight: '900',
  },
  dropdownContainer: {
    width: '100%',
    height: '100%',
  },
  webSelectWrapper: {
    width: '100%',
    height: '100%',
    position: 'relative',
  },
  webSelectButton: {
    width: '100%',
    height: '100%',
    minHeight: 48,
    justifyContent: 'center',
    alignItems: 'flex-start',
    position: 'relative',
    backgroundColor: 'white',
    borderWidth: 1,
    borderColor: '#000000',
    borderRadius: 4,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      outlineStyle: 'none',
    }),
  },
  webSelectText: {
    fontSize: 13,
    fontFamily: 'Archivo',
    width: '100%',
    lineHeight: 19.5,
    ...(Platform.OS === 'web' && {
      whiteSpace: 'normal',
      wordWrap: 'break-word',
      overflowWrap: 'break-word',
    }),
  },
  webSelectArrow: {
    position: 'absolute',
    right: 12,
    top: '45%',
    pointerEvents: 'none',
  },
  webSelectArrowText: {
    fontSize: 12,
  },
  webDropdownMenu: {
    position: 'absolute',
    left: 0,
    borderWidth: 2,
    borderColor: '#000000',
    borderRadius: 8,
    maxHeight: 200,
    minWidth: 180,
    zIndex: 99999,
    backgroundColor: 'white',
    ...(Platform.OS === 'web' && {
      boxShadow: '4px 6px 12px rgba(0, 0, 0, 0.25)',
      overflow: 'visible',
    }),
    shadowColor: '#000',
    shadowOffset: { width: 4, height: 6 },
    shadowOpacity: 0.25,
    shadowRadius: 12,
    elevation: 20,
  },
  dropdownOuterWrapper: {
    position: 'relative',
    minHeight: 44,
    justifyContent: 'center',
    borderBottomWidth: 1,
    borderColor: 'rgba(0,0,0,0.1)',
    ...(Platform.OS === 'web' && {
      overflow: 'visible',
    }),
  },
  dropdownInnerWrapper: {
    position: 'relative',
    width: '100%',
    ...(Platform.OS === 'web' && {
      overflow: 'visible',
    }),
  },
  webDropdownMenuBelow: {
    top: '100%',
    marginTop: 4,
  },
  webDropdownMenuAbove: {
    bottom: '100%',
    marginBottom: 4,
  },
  webDropdownScroll: {
    maxHeight: 300,
    backgroundColor: 'white',
  },
  webDropdownOption: {
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.1)',
    backgroundColor: 'white',
  },
  webDropdownOptionText: {
    fontSize: 13,
    fontFamily: 'Archivo',
    lineHeight: 19.5,
    color: '#000000',
    ...(Platform.OS === 'web' && {
      whiteSpace: 'normal',
      wordWrap: 'break-word',
      overflowWrap: 'break-word',
    }),
  },
  dropdown: {
    width: '100%',
    height: '100%',
  },
  dropdownButton: {
    borderRadius: 4,
    borderWidth: 1,
    minHeight: 48,
    justifyContent: 'center',
    width: '100%',
    height: '100%',
  },
  dropdownButtonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    gap: 8,
  },
  dropdownText: {
    fontSize: 13,
    fontFamily: 'Archivo',
    flex: 1,
  },
  dropdownArrow: {
    fontSize: 12,
    marginLeft: 4,
  },
  rankBadge: {
    width: 28,
    height: 28,
    borderRadius: 14,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 8,
  },
  rankBadgeWeb: {
    position: 'absolute',
    left: 12,
    top: 10,
    marginRight: 0,
  },
  rankBadgeText: {
    color: '#fff',
    fontSize: 14,
    fontFamily: 'Chivo',
    fontWeight: '700',
  },
  dropdownDescription: {
    marginTop: 8,
    padding: 8,
    backgroundColor: 'rgba(255,255,255,0.02)',
    borderRadius: 4,
  },
  dropdownDescriptionText: {
    fontSize: 11,
    fontFamily: 'Archivo',
    lineHeight: 16,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.7)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    borderRadius: 8,
    padding: 8,
    minWidth: 300,
    maxWidth: '90%',
    maxHeight: '80%',
  },
  dropdownOption: {
    padding: 12,
    borderRadius: 4,
    marginBottom: 4,
    borderWidth: 1,
  },
  dropdownOptionText: {
    fontSize: 12,
    fontFamily: 'Archivo',
  },
  scoreText: {
    fontSize: 14,
    fontFamily: 'Chivo',
    fontWeight: '700',
  },
  addColumn: {
    width: 150,
    padding: 12,
    justifyContent: 'flex-start',
  },
  addInitiativeButton: {
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  addInitiativeButtonStyled: {
    padding: 12,
    borderRadius: 10,
    alignItems: 'center',
    backgroundColor: '#FF4747',
    borderWidth: 3,
    borderColor: '#000000',
    ...Platform.select({
      web: {
        boxShadow: '3px 4px 0px rgba(0,0,0,0.85)',
        cursor: 'pointer',
      },
    }),
  },
  addInitiativeText: {
    color: '#fff',
    fontSize: 12,
    fontFamily: 'Archivo',
    fontWeight: '700',
  },
  columnHeaderText: {
    fontSize: 10,
    fontFamily: 'Archivo',
    fontWeight: '600',
    textAlign: 'center',
    flex: 1,
  },
  resultsSection: {
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
    marginTop: 16,
    marginBottom: 16,
  },
  resultsTitle: {
    fontSize: 18,
    fontFamily: 'Chivo',
    fontWeight: '700',
    marginBottom: 12,
  },
  resultRow: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 4,
    borderWidth: 1,
    marginBottom: 8,
    gap: 8,
  },
  resultRank: {
    fontSize: 14,
    fontFamily: 'Chivo',
    fontWeight: '700',
    minWidth: 100,
  },
  resultTitle: {
    fontSize: 14,
    fontFamily: 'Archivo',
    flex: 1,
  },
  resultScore: {
    fontSize: 14,
    fontFamily: 'Chivo',
    fontWeight: '700',
  },
  // Strategic Action Matrix styles
  legend: {
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    marginBottom: 16,
  },
  legendTitle: {
    fontSize: 14,
    fontFamily: 'Chivo',
    fontWeight: '700',
    marginBottom: 8,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 4,
  },
  legendColor: {
    width: 16,
    height: 16,
    borderRadius: 4,
    marginRight: 8,
  },
  legendText: {
    fontSize: 13,
    fontFamily: 'Archivo',
  },
  actionCard: {
    padding: 16,
    borderRadius: 8,
    marginBottom: 16,
  },
  actionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  actionTitle: {
    fontSize: 16,
    fontFamily: 'Chivo',
    fontWeight: '700',
    flex: 1,
  },
  matrixInputs: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 12,
    alignItems: 'flex-end',
  },
  matrixInputGroup: {
    flex: 1,
  },
  matrixLabel: {
    fontSize: 12,
    fontFamily: 'Archivo',
    marginBottom: 4,
  },
  matrixInput: {
    padding: 8,
    borderRadius: 4,
    borderWidth: 1,
    fontSize: 14,
    fontFamily: 'Archivo',
    textAlign: 'center',
  },
  categoryBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 8,
    borderRadius: 4,
    backgroundColor: 'rgba(255,255,255,0.05)',
  },
  categoryDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 6,
  },
  categoryText: {
    fontSize: 12,
    fontFamily: 'Archivo',
    fontWeight: '600',
  },
  // Tiger Team Breakouts styles
  departmentManagerSection: {
    padding: 16,
    backgroundColor: 'rgba(0,0,0,0.03)',
    borderRadius: 12,
    marginBottom: 16,
    borderWidth: 2,
    borderColor: '#000000',
  },
  departmentManagerTitle: {
    fontSize: 14,
    fontFamily: 'Chivo',
    fontWeight: '700',
    marginBottom: 8,
    color: '#000000',
  },
  departmentTagsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 12,
  },
  departmentTagPill: {
    backgroundColor: '#E0F2FE',
    paddingVertical: 4,
    paddingHorizontal: 12,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#000000',
  },
  departmentTagPillText: {
    fontSize: 12,
    fontFamily: 'Archivo',
    fontWeight: '600',
    color: '#000000',
  },
  addDepartmentInputRow: {
    flexDirection: 'row',
    gap: 8,
  },
  addDepartmentTextInput: {
    flex: 1,
    backgroundColor: 'white',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: 13,
    fontFamily: 'Archivo',
    borderWidth: 2,
  },
  addDepartmentBtn: {
    backgroundColor: '#4ADE80',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 2,
    borderColor: '#000000',
    justifyContent: 'center',
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  addDepartmentBtnText: {
    fontSize: 13,
    fontFamily: 'Archivo',
    fontWeight: '700',
    color: '#FFFFFF',
  },
  tabNavigation: {
    flexDirection: 'row',
    marginBottom: 16,
    gap: 8,
    flexWrap: 'wrap',
  },
  tabButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 10,
    marginRight: 8,
    marginBottom: 8,
    borderWidth: 2,
    borderColor: '#000000',
    ...Platform.select({
      web: {
        boxShadow: '2px 3px 0px rgba(0,0,0,0.8)',
        cursor: 'pointer',
      },
    }),
  },
  tabButtonText: {
    fontSize: 14,
    fontFamily: 'Chivo',
    fontWeight: '700',
  },
  tabContent: {
    flex: 1,
    marginBottom: 16,
  },
  // Tiger Team Breakouts Table styles (similar to Strategic Action Matrix)
  tigerTableContainer: {
    flex: 1,
    marginBottom: 16,
  },
  tigerHorizontalScroll: {
    flex: 1,
    ...(Platform.OS === 'web' && {
      overflow: 'visible',
    }),
  },
  tigerMatrixContainer: {
    flexDirection: 'column',
    minWidth: '100%',
    borderRadius: 14,
    borderWidth: 3,
    borderColor: '#000000',
    backgroundColor: 'white',
    ...Platform.select({
      web: {
        boxShadow: '4px 5px 0px rgba(0,0,0,0.85)',
      },
    }),
  },
  tigerFixedHeaderRow: {
    flexDirection: 'row',
    zIndex: 100,
    borderBottomWidth: 3,
    borderBottomColor: '#000000',
    backgroundColor: '#FF4747',
  },
  tigerHeaderColumn: {
    borderBottomWidth: 0,
  },
  tigerVerticalScrollContent: {
    flex: 1,
    ...(Platform.OS === 'web' && {
      overflow: 'visible',
    }),
  },
  tigerContentRowsContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    ...(Platform.OS === 'web' && {
      overflow: 'visible',
    }),
  },
  tigerHeader: {
    height: 60,
    padding: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  tigerHeaderText: {
    fontSize: 13,
    fontFamily: 'Chivo',
    fontWeight: '700',
    textAlign: 'center',
  },
  tigerRow: {
    height: 60,
    width: '100%',
  },
  tigerCellInput: {
    width: '100%',
    height: '100%',
    padding: 8,
    borderBottomWidth: 1,
    fontSize: 13,
    fontFamily: 'Archivo',
  },
  tigerDropdown: {
    width: '100%',
    height: '100%',
  },
  tigerRemoveButton: {
    padding: 4,
    justifyContent: 'center',
    alignItems: 'center',
  },
  // All Staff Column widths
  tigerPersonColumn: {
    width: 180,
    borderWidth: 1,
  },
  tigerTitleColumn: {
    width: 200,
    borderWidth: 1,
    borderLeftWidth: 0,
  },
  tigerDepartmentColumn: {
    width: 140,
    borderWidth: 1,
    borderLeftWidth: 0,
  },
  tigerReportsToColumn: {
    width: 180,
    borderWidth: 1,
    borderLeftWidth: 0,
  },
  tigerTypeColumn: {
    width: 130,
    borderWidth: 1,
    borderLeftWidth: 0,
  },
  tigerActiveColumn: {
    width: 80,
    borderWidth: 1,
    borderLeftWidth: 0,
  },
  tigerFLSAColumn: {
    width: 140,
    borderWidth: 1,
    borderLeftWidth: 0,
  },
  tigerActionColumn: {
    width: 80,
    borderWidth: 1,
    borderLeftWidth: 0,
  },
  tigerCellText: {
    fontSize: 12,
    fontFamily: 'Archivo',
    textAlign: 'center',
  },
  // Department Tab Column widths
  tigerDeptEmployeeColumn: {
    width: 180,
    borderWidth: 1,
  },
  tigerDeptTitleColumn: {
    width: 200,
    borderWidth: 1,
    borderLeftWidth: 0,
  },
  tigerDeptDepartmentColumn: {
    width: 140,
    borderWidth: 1,
    borderLeftWidth: 0,
  },
  tigerDeptReportsToColumn: {
    width: 180,
    borderWidth: 1,
    borderLeftWidth: 0,
  },
  tigerDeptTypeColumn: {
    width: 130,
    borderWidth: 1,
    borderLeftWidth: 0,
  },
  tigerDeptActiveColumn: {
    width: 80,
    borderWidth: 1,
    borderLeftWidth: 0,
  },
  tigerDeptQ1MeasureColumn: {
    width: 150,
    borderWidth: 1,
    borderLeftWidth: 0,
  },
  tigerDeptQ1ScoreColumn: {
    width: 100,
    borderWidth: 1,
    borderLeftWidth: 0,
  },
  tigerDeptQ1WeightColumn: {
    width: 80,
    borderWidth: 1,
    borderLeftWidth: 0,
  },
  tigerDeptQ2MeasureColumn: {
    width: 150,
    borderWidth: 1,
    borderLeftWidth: 0,
  },
  tigerDeptQ2ScoreColumn: {
    width: 100,
    borderWidth: 1,
    borderLeftWidth: 0,
  },
  tigerDeptQ2WeightColumn: {
    width: 80,
    borderWidth: 1,
    borderLeftWidth: 0,
  },
  tigerDeptQ3MeasureColumn: {
    width: 150,
    borderWidth: 1,
    borderLeftWidth: 0,
  },
  tigerDeptQ3ScoreColumn: {
    width: 100,
    borderWidth: 1,
    borderLeftWidth: 0,
  },
  tigerDeptQ3WeightColumn: {
    width: 80,
    borderWidth: 1,
    borderLeftWidth: 0,
  },
  tigerDeptQual1MeasureColumn: {
    width: 150,
    borderWidth: 1,
    borderLeftWidth: 0,
  },
  tigerDeptQual1ScoreColumn: {
    width: 100,
    borderWidth: 1,
    borderLeftWidth: 0,
  },
  tigerDeptQual1WeightColumn: {
    width: 80,
    borderWidth: 1,
    borderLeftWidth: 0,
  },
  tigerDeptIndividualScoreColumn: {
    width: 130,
    borderWidth: 1,
    borderLeftWidth: 0,
  },
  tigerDeptDirectReportsColumn: {
    width: 150,
    borderWidth: 1,
    borderLeftWidth: 0,
  },
  tigerDeptFinalScoreColumn: {
    width: 120,
    borderWidth: 1,
    borderLeftWidth: 0,
  },
  staffCard: {
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    marginBottom: 8,
  },
  staffRow: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  staffInput: {
    flex: 1,
    minWidth: 120,
    padding: 8,
    borderRadius: 4,
    borderWidth: 1,
    fontSize: 13,
    fontFamily: 'Archivo',
  },
  staffDropdown: {
    minWidth: 120,
  },
  departmentTable: {
    minWidth: 2000,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: 'rgba(255,255,255,0.05)',
    padding: 12,
    borderBottomWidth: 2,
    borderBottomColor: 'rgba(255,255,255,0.2)',
  },
  tableHeaderText: {
    fontSize: 12,
    fontFamily: 'Chivo',
    fontWeight: '700',
    minWidth: 120,
    marginRight: 8,
  },
  tableRow: {
    flexDirection: 'row',
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.1)',
  },
  tableCell: {
    fontSize: 12,
    fontFamily: 'Archivo',
    minWidth: 120,
    marginRight: 8,
    paddingVertical: 4,
  },
  tableCellInput: {
    fontSize: 12,
    fontFamily: 'Archivo',
    minWidth: 120,
    marginRight: 8,
    padding: 8,
    borderRadius: 4,
    borderWidth: 1,
  },
  summarySection: {
    marginBottom: 24,
  },
  summaryTitle: {
    fontSize: 18,
    fontFamily: 'Chivo',
    fontWeight: '700',
    marginBottom: 12,
  },
  summaryTable: {
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.1)',
    borderRadius: 8,
    overflow: 'hidden',
  },
  summaryHeader: {
    flexDirection: 'row',
    backgroundColor: 'rgba(255,255,255,0.05)',
    padding: 12,
    borderBottomWidth: 2,
    borderBottomColor: 'rgba(255,255,255,0.2)',
  },
  summaryHeaderText: {
    fontSize: 13,
    fontFamily: 'Chivo',
    fontWeight: '700',
    flex: 1,
  },
  summaryRow: {
    flexDirection: 'row',
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255,255,255,0.1)',
  },
  summaryCell: {
    fontSize: 13,
    fontFamily: 'Archivo',
    flex: 1,
  },
  participantCard: {
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    marginBottom: 8,
    position: 'relative',
  },
  breakoutCard: {
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
    marginBottom: 16,
  },
  breakoutHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  breakoutNumber: {
    fontSize: 16,
    fontFamily: 'Chivo',
    fontWeight: '700',
  },
  breakoutMeta: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 12,
  },
  metaGroup: {
    flex: 1,
  },
  metaLabel: {
    fontSize: 12,
    fontFamily: 'Archivo',
    marginBottom: 4,
  },
  metaInput: {
    padding: 8,
    borderRadius: 4,
    borderWidth: 1,
    fontSize: 14,
    fontFamily: 'Archivo',
  },
  actionItemsSection: {
    marginTop: 16,
  },
  actionItemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 8,
    borderRadius: 4,
    borderWidth: 1,
    marginTop: 8,
  },
  // Strategic Action Matrix Results styles
  actionMatrixContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  yAxisContainer: {
    width: 40,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
    paddingTop: 60, // Align with quadrants
    height: '100%',
  },
  yAxisLabel: {
    fontSize: 14,
    fontFamily: 'Chivo',
    fontWeight: '700',
    transform: [{ rotate: '-90deg' }],
    textAlign: 'center',
  },
  xAxisContainer: {
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
    width: '100%',
  },
  axisLabel: {
    fontSize: 14,
    fontFamily: 'Chivo',
    fontWeight: '700',
  },
  quadrantsGrid: {
    flex: 1,
  },
  quadrantsRow: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 16,
  },
  quadrantBox: {
    flex: 1,
    minHeight: 200,
    padding: 16,
    borderRadius: 8,
    borderWidth: 2,
    backgroundColor: 'rgba(255,255,255,0.03)',
  },
  quadrantTitle: {
    fontSize: 16,
    fontFamily: 'Chivo',
    fontWeight: '700',
    marginBottom: 4,
  },
  quadrantSubtitle: {
    fontSize: 12,
    fontFamily: 'Archivo',
    marginBottom: 12,
  },
  quadrantTasks: {
    maxHeight: 150,
  },
  quadrantTaskItem: {
    padding: 8,
    borderRadius: 4,
    marginBottom: 8,
  },
  quadrantTaskText: {
    fontSize: 13,
    fontFamily: 'Archivo',
  },
});


